"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  useParams,
  useNavigate,
  useOutletContext,
  useLocation,
} from "react-router-dom";
import { useFuncion } from "../context/FuncionContext";
import LoadingSpinner from "../components/LoadingSpinner";
import cacheManager from "../utils/cacheManager";
import { useAuth } from "../context/AuthContext";
import ProgressRing from "../components/ui/ProgressRing";
import {
  BookOpen,
  Layers,
  Package,
  CheckSquare,
  Play,
  Lock,
  ChevronDown,
  ChevronUp,
  Filter,
  Info,
  FolderOpen,
  CalendarDays,
  Clock,
  ShieldCheck,
  Trophy,
  Eye,
  EyeOff,
  FileDown,
  Settings,
  FileText,
  Edit,
} from "lucide-react";

import StreakModal from "../components/gamification/StreakModal";
import GamificationStats from "../components/gamification/GamificationStats";

/* =========================================================
   Utils
========================================================= */
const activeFetches = new Map();

// ✅ Deduplicación de requests para evitar llamadas repetidas al mismo curso/estructura
const inFlightCourse = new Map();
const inFlightStructure = new Map();

const decodeJwtPayload = (token) => {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const getUserKeyFromStorage = () => {
  try {
    // ajusta estos nombres a los que uses en tu app
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("accessToken");

    const payload = decodeJwtPayload(token);
    // usa lo que exista en tu JWT (sub/id/email)
    return (
      payload?.sub ||
      payload?.id ||
      payload?.user_id ||
      payload?.email ||
      payload?.codigo_institucional ||
      "anon"
    );
  } catch {
    return "anon";
  }
};

const toNumber = (v, d = 0) => {
  const n = typeof v === "string" ? Number.parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : d;
};
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const safeJSONParse = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const safeKey = (s) =>
  String(s || "")
    .replace(/[^a-z0-9_-]/gi, "")
    .slice(0, 48);

const getTutorialCacheKey = (codigoCurso, userNameOrId) =>
  `wt:tutorial:v1:${safeKey(codigoCurso)}:${safeKey(userNameOrId)}`;

// ✅ Helper para interpretar banderas tipo secuencia_hijos
const flagToBoolean = (value, defaultValue = true) => {
  if (value === undefined || value === null) return !!defaultValue;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (["true", "1", "yes", "si", "sí", "on", "t"].includes(v)) return true; // 👈 incluye 't'
    if (["false", "0", "no", "off", "f"].includes(v)) return false; // 👈 incluye 'f'
  }
  return !!defaultValue;
};

// ✅ Alias robustos usados por LevelTestGate
const asBool = (value, defaultValue = false) =>
  flagToBoolean(value, defaultValue);

const asNum = (value, defaultValue = 0) =>
  toNumber(value, defaultValue);

// Hash rápido para comparar payloads grandes sin deep-equal
const hashString = (str) => {
  // djb2 xor variant
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return (h >>> 0).toString(16);
};

const getFirstComponentIdFromStructure = (structure = []) => {
  const stack = Array.isArray(structure) ? [...structure] : [];
  while (stack.length) {
    const node = stack.shift();
    if (!node) continue;
    if (node.tipo === "componente") return node.id ?? null;
    if (Array.isArray(node.children) && node.children.length) {
      stack.unshift(...node.children);
    }
  }
  return null;
};

/* =========================================================
   CSS (mismo diseño, solo movido a constantes)
========================================================= */
const CSS_EXAM_SOLO = `
  .exSolo-wrap{ padding: clamp(16px, 2vw, 28px); max-width: 980px; margin: 0 auto; }

  .exSolo-hero{
    position: relative;
    overflow: hidden;
    border-radius: 20px;
    border: 1px solid #e5e7eb;
    background:
      radial-gradient(1200px 300px at 10% -10%, rgba(59,130,246,.12), transparent 60%),
      radial-gradient(900px 260px at 90% 10%, rgba(99,102,241,.12), transparent 60%),
      linear-gradient(135deg, #f7fbff 0%, #ffffff 100%);
    box-shadow: 0 10px 30px rgba(0,0,0,.06);
    padding: clamp(20px, 3vw, 36px);
  }
  .exSolo-head{
    display: grid; grid-template-columns: 1fr auto; gap: 16px; align-items: start;
  }
  .exSolo-title{
    display:flex; align-items:center; gap:12px;
  }
  .exSolo-title h1{
    margin:0; font-size: clamp(20px, 3.2vw, 28px); color:#0f172a; letter-spacing:.2px;
  }
  .exSolo-badge{
    display:inline-flex; align-items:center; gap:8px;
    background:#fff; border:1px dashed #c7d2fe; color:#4338ca;
    padding:6px 10px; border-radius:999px; font-weight:700; font-size:.85rem;
  }

  .exSolo-sub{ color:#475569; margin:8px 0 0; line-height:1.6; max-width: 760px; }

  .exSolo-grid{
    display:grid; gap:14px; margin-top:16px;
    grid-template-columns: repeat(12, 1fr);
  }
  .exSolo-col-left{ grid-column: span 8; min-width: 0; }
  .exSolo-col-right{ grid-column: span 4; min-width: 0; }

  @media (max-width: 960px){
    .exSolo-head{ grid-template-columns: 1fr; }
    .exSolo-grid{ grid-template-columns: 1fr; }
    .exSolo-col-left, .exSolo-col-right{ grid-column: auto; }
  }

  .exSolo-card{
    background:#fff; border:1px solid #e5e7eb; border-radius:16px; padding:14px;
    box-shadow: 0 6px 18px rgba(0,0,0,.05);
  }
  .exSolo-cardHeader{
    display:flex; align-items:center; gap:10px; margin-bottom:10px;
  }
  .exSolo-iconBox{
    width:36px; height:36px; border-radius:10px;
    display:flex; align-items:center; justify-content:center;
    border:1px solid #dbeafe; background:#eff6ff; color:#1d4ed8;
  }
  .exSolo-cardTitle{ margin:0; font-size: .95rem; color:#0f172a; }
  .exSolo-meta{
    display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap:10px;
  }
  @media (max-width: 420px){ .exSolo-meta{ grid-template-columns: 1fr; } }

  .exSolo-item{
    display:flex; align-items:center; gap:10px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:12px; padding:10px;
  }
  .exSolo-item strong{ color:#0f172a; font-size:.95rem; }
  .exSolo-item span{ color:#475569; font-size:.9rem; }

  .exSolo-steps{
    list-style:none; margin: 4px 0 0; padding:0; display:grid; gap:8px;
  }
  .exSolo-step{
    display:flex; gap:10px; align-items:flex-start; background:#f9fafb; border:1px solid #e5e7eb;
    border-radius:12px; padding:10px;
  }
  .exSolo-step p{ margin:0; color:#475569; line-height:1.5; }

  .exSolo-ctaRow{
    display:flex; flex-wrap: wrap; align-items:center; gap:10px; margin-top:14px;
  }
  .exSolo-btn{
    padding: 12px 22px; border:none; border-radius:12px; font-weight:800; font-size:15px; cursor:pointer; transition: all .18s ease;
    display:inline-flex; align-items:center; gap:8px;
  }
  .exSolo-btn--primary{
    background: linear-gradient(135deg,#2563eb,#1d4ed8); color:#fff; border:1px solid #1d4ed8;
    box-shadow: 0 12px 28px rgba(37,99,235,.22), inset 0 1px 0 rgba(255,255,255,.35);
  }
  .exSolo-btn--primary:hover{ transform: translateY(-1px); filter: brightness(1.05); }
  .exSolo-btn--ghost{
    background:#fff; color:#0f172a; border:1px solid #e5e7eb;
  }
  .exSolo-btn--ghost:hover{ background:#f9fafb; transform: translateY(-1px); }

  .exSolo-hint{
    display:flex; align-items:center; gap:8px; color:#64748b; font-size:.9rem; margin-top:10px;
  }
`;

const CSS_MAIN = `
  :root{
    --radius: 16px;
    --shadow-1: 0 6px 24px rgba(0,0,0,.06);
    --shadow-2: 0 12px 38px rgba(0,0,0,.08);
    --text: #1f2937;
    --muted: #6b7280;
    --border: #e5e7eb;
    --bg: #fafafa;
    --brand: #0f5fb8;
    --ring-glow:#a5b4fc;
    --comp-card-min: 196px;   /* ✅ mínimo tarjeta */
  --comp-card-max: 240px;   /* ✅ máximo tarjeta */
  }
 .course-detail{
  padding: clamp(16px, 2vw, 28px);
  width: min(1180px, 100%);
  min-width: 360px;              
  margin: 0 auto;
  color: var(--text);
  box-sizing: border-box;
}

/* evita scroll horizontal por algo que se salga */
.course-detail, .course-structure, .level-section, .module-section{
  overflow-x: clip;
}

.hero-sentinel{ position: relative; width:100%; height:1px; }
.hero-stick{
  position: sticky;
  top: calc(env(safe-area-inset-top, 0px));
  z-index: 30;
  backdrop-filter: blur(6px) saturate(1.05);
  -webkit-backdrop-filter: blur(6px);
  padding-top: 8px;
  padding-bottom: 8px;
}



.hero-simple{
  gap: 10px;
}

/* fila superior */
.hero-topRow{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-title{
  margin-top: 0px;
  color: var(--brand);
  font-size: clamp(22px, 3.2vw, 32px);
  font-weight: 900;
  letter-spacing: .2px;
}

/* acciones a la derecha: código + ojo */
.hero-actions{
  display:flex;
  align-items:center;
  gap: 10px;
}

/* la descripción visible como en screenshot */
.hero-desc-always{
  margin: 0;
  color: var(--muted);
  line-height: 1.6;
}

.hero-stick:not(.is-stuck) .hero-left-card{
  height: 140px;
  overflow: hidden;
}

.hero-stick:not(.is-stuck) .hero-left-card.is-open{
  min-height: 140px;
  overflow: visible;
}
.hero-left-card.is-open .hero-left-header{
  align-items: flex-start;
}

.hero-left-header{
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto; 
  gap: 16px;
  align-items: center; 
}

@media (max-width: 720px){
  .hero-left-header{
    grid-template-columns: 1fr;
  }
}


.hero-extra-inline{
  margin-top: 35px;
  overflow: hidden;
}

/* Si quieres aún más pegado, baja a 4px */
.hero-extra-inline .hero-desc{
  margin: 10;                  
  padding: auto;
}

/* hint pegadito también */
.hero-extra-inline .hero-hints{
  margin-top: 10px;
  display:flex;
  align-items:center;
  gap:10px;
}



/* ===== ESTADOS STUCK ===== */
.hero-stick.is-stuck .hero-left-card,
.hero-stick.is-stuck .hero-right-card{
  padding: 8px 12px !important;
  border-radius: 14px !important;
}

.hero-stick.is-stuck .hero-left h1{
  font-size: 18px !important;
  line-height: 1.05 !important;
}
.hero-stick.is-stuck .hero-extra-area{ display: none !important; }
.hero-stick.is-stuck .course-ring{ width: 96px !important; height: 96px !important; }

.hero-stick.is-stuck .stat-card{
  padding: 8px 10px !important;
  min-width: 120px !important;
  gap: 8px !important;
}
.hero-stick.is-stuck .stat-icon{ width: 30px !important; height: 30px !important; }
.hero-stick.is-stuck .stat-value{ font-size: 1rem !important; }
.hero-stick.is-stuck .stat-label{ font-size: .72rem !important; }
.hero-stick.is-stuck .code-pill{ padding: 4px 8px !important; font-size: .74rem !important; }
.hero-stick.is-stuck .mini-link{ padding: 4px 8px !important; }

.hero-title-area{ 
  display: flex;
  flex-direction: column;
  gap: 0px;
  margin-top: 0px;
}

/* ===== AGRUPADOR DE GAMIFICACIÓN ===== */
.gami-wrap{
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 24px;
}

@media (max-width: 560px){
  .gami-wrap{
    flex-wrap:wrap;
    justify-content:center;
  }
}

.course-ring{
  display:flex;
  align-items:center;
  justify-content:center;
}

  .hero-left h1{
    margin:0;
    color: var(--brand);
    font-size: clamp(20px, 3vw, 28px);
    letter-spacing:.2px;
    display:flex;
    align-items:center;
    line-height:1.15;
  }

  .title-inline{ display:inline-flex; align-items:center; gap:8px; margin-left:10px; flex-wrap:wrap; }
  .hero-desc{ color: var(--muted); max-width: 720px; line-height: 1.6; margin-top: 0; padding: 0;  }

  .code-pill{
    display:inline-flex; align-items:center; gap:8px;
    background:#fff; border:1px dashed #c7d2fe; color:#4338ca;
    padding:6px 10px; border-radius: 999px; font-weight:700; font-size:.82rem;
  }

  .course-ring{ width:140px; height:140px; transition: transform .18s ease; margin-right: 50px; }
  .hero-stick.is-stuck .course-ring{ transform: scale(.86); }
  @media (max-width: 480px){
    .course-ring{ width:116px; height:116px; }
    .hero-stick.is-stuck .course-ring{ transform: scale(.9); }
  }

  .mini-link{
    appearance:none; background:#fff; border:1px solid #c7d2fe; color:#1d4ed8; font-weight:800; font-size:.85rem;
    display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px; cursor:pointer;
    box-shadow:0 8px 18px rgba(59,130,246,.12); transition: transform .18s, background .18s, box-shadow .18s, border-color .18s;
  }
  .mini-link:hover{ transform:translateY(-1px); background:#eff6ff; border-color:#93c5fd; box-shadow:0 12px 24px rgba(59,130,246,.18); }
  .mini-link:focus{ outline:2px solid #c7d2fe; outline-offset:2px; }
  .mini-link svg{ width:16px; height:16px; }
  .pulse-eye{ animation: pulseGlow 1.6s ease-in-out infinite; }
  @keyframes pulseGlow{ 0%,100%{ box-shadow:0 8px 18px rgba(59,130,246,.12);} 50%{ box-shadow:0 10px 26px rgba(59,130,246,.22);} }

  .continue-fab{
  position: fixed;
  right: calc(16px + env(safe-area-inset-right, 0px));
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 50;

  display:inline-flex;
  align-items:center;
  gap:10px;

  padding: 10px 12px;
  border-radius: 999px;

  border: 1px solid rgba(15, 95, 184, .22);
  background: rgba(255,255,255,.92);
  backdrop-filter: blur(10px) saturate(1.15);
  -webkit-backdrop-filter: blur(10px);

  box-shadow: 0 16px 40px rgba(2, 6, 23, .14);
  cursor:pointer;
  transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease, background .12s ease;

  max-width: min(420px, calc(100vw - 32px));
}

.continue-fab:hover{
  transform: translateY(-1px);
  box-shadow: 0 20px 52px rgba(2, 6, 23, .18);
  border-color: rgba(15, 95, 184, .34);
  background: rgba(255,255,255,.98);
}
.continue-fab:active{ transform: translateY(0); }
.continue-fab:focus{
  outline: 3px solid rgba(165, 180, 252, .75);
  outline-offset: 2px;
}

.continue-fab .cf-ico{
  width: 34px; height: 34px;
  border-radius: 999px;
  display:flex; align-items:center; justify-content:center;
  background: linear-gradient(135deg, rgba(37,99,235,.14), rgba(29,78,216,.06));
  border: 1px solid rgba(37,99,235,.18);
  color: #1d4ed8;
  flex: 0 0 auto;
}

.continue-fab.is-up .cf-ico{
  background: linear-gradient(135deg, rgba(16,185,129,.12), rgba(16,185,129,.05));
  border-color: rgba(16,185,129,.18);
  color: #059669;
}

.continue-fab .cf-text{
  min-width: 0;
  display:flex;
  flex-direction:column;
  gap:2px;
}

.continue-fab .cf-title{
  font-weight: 900;
  color: #0f172a;
  font-size: .92rem;
  letter-spacing: .2px;
  line-height: 1.05;
}

.continue-fab .cf-sub{
  color: #475569;
  font-size: .80rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}

@media (max-width: 480px){
  .continue-fab .cf-sub{ max-width: 190px; }
}



  .scroll-highlight{
    outline: 3px solid #22c55e;
    border-radius: 16px;
    animation: sh 1.2s ease-out 1;
  }
  @keyframes sh{
    0%{ outline-color: rgba(34,197,94,0); }
    20%{ outline-color: rgba(34,197,94,.9); }
    100%{ outline-color: rgba(34,197,94,0); }
  }

  .cr-title{ font-size:.8rem; color: var(--muted); }
  .stat-card{ position:relative; background:#fff; border:1px solid var(--border); border-radius: 14px; padding:12px 14px; min-width: 150px; display:flex; align-items:center; gap:10px; box-shadow: var(--shadow-1); }
  .stat-btn{ cursor:pointer; transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease, background .12s ease; }
  .stat-btn:hover{ transform: translateY(-1px); box-shadow:0 10px 22px rgba(0,0,0,.08); border-color:#cbd5e1; background:#f8fafc; }
  .stat-icon{ width:36px; height:36px; border-radius:999px; background:#f1f5ff; display:flex; align-items:center; justify-content:center; }
  .stat-value{ font-weight:800; color: var(--brand); line-height:1; }
  .stat-label{ color: var(--muted); font-size:.8rem; }

 /* ===== NIVEL ===== */
 .level-section{
    background: rgba(239,246,255,0.10);
    border: 1px solid #e5e7eb;
    border-radius: 24px;
    overflow: hidden;
    margin: 20px 0 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    transition: all .3s ease;
  }
  .level-header{
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 20px;
    background: #fff;
    border-bottom: 1px solid #f3f4f6;
    flex-wrap: wrap;
  }

  @media (min-width: 768px){
    .level-header{
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  .level-title{
    display: flex;
    align-items: center;
    gap: 10px;
    flex-grow: 1;
    min-width: 0;
  }

  .level-icon{
    width: 36px; height: 36px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    border: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .level-name{
    margin: 0;
    font-size: clamp(15px, 2vw, 18px);
    font-weight: 800;
    color: #1e3a8a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .level-progress-inline{
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 180px;
  }

  .level-report-btn{
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #374151;
    cursor: pointer;
    transition: transform .12s ease, box-shadow .12s ease;
  }
  .level-report-btn:hover{ transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,.08); }
  .level-report-btn:disabled{ opacity:.5; cursor:not-allowed; transform:none; box-shadow:none; }

  .filters-inline{
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .progress-text{
    color: var(--muted);
    font-size: .8rem;
    font-weight: 800;
    min-width: 36px;
    text-align: right;
    white-space: nowrap;
  }

  /* Barra de progreso con gradiente dinámico */
  .lp-wrap{ display:flex; align-items:center; gap:10px; flex:1; }
  .lp-track{
    flex: 1;
    height: 10px;
    background: #f1f5f9;
    border-radius: 999px;
    overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(0,0,0,.04);
  }
  .lp-fill{
    height: 100%;
    border-radius: 999px;
    transition: width .6s ease;
    background: linear-gradient(90deg, #60a5fa, #3b82f6);
  }

  /* ===== MÓDULOS ===== */
  .modules-list{ display: grid; gap: 8px; padding: 8px 12px 12px; }
  .modules-list.collapsed{ display: none; }

  .module-section{
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 14px 16px 10px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    transition: box-shadow .2s ease;
  }
  .module-section:hover{ box-shadow: 0 4px 14px rgba(0,0,0,0.07); }

  .module-header{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;
    flex-wrap: wrap;
  }

  .module-title{
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 200px;
    flex-grow: 1;
  }

  /* Punto de color del módulo (type-dot) más visible */
  .type-dot{
    width: 8px; height: 8px;
    border-radius: 999px;
    flex-shrink: 0;
    background: #1e3a8a;
  }

  .module-title h3{
    font-weight: 800;
    font-size: clamp(13px, 1.5vw, 15px);
    color: #1e3a8a;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .module-toolbar{
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
    flex: 1 1 auto;
    min-width: 260px;
  }

  .module-progress{
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 180px;
    max-width: 380px;
    flex: 1 1 220px;
  }

  /* Chips de filtro */
  .chip{
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #374151;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: .8rem;
    font-weight: 700;
    transition: all .18s ease;
    cursor: pointer;
  }
  .chip:hover{ transform: translateY(-1px); box-shadow: 0 4px 10px rgba(0,0,0,.06); background: #f8fafc; }
  .chip.active{ background: #1e3a8a; color: #fff; border-color: #1e3a8a; }

  .ghost-btn{
    background: transparent;
    border: 1px solid #e5e7eb;
    padding: 5px;
    border-radius: 8px;
    cursor: pointer;
    transition: all .18s ease;
    display: flex; align-items: center; justify-content: center;
  }
  .ghost-btn:hover{ background: #f8fafc; border-color: #cbd5e1; }

  .muted{ color: var(--muted); font-size: .88rem; }
  .mtop{ margin-top: 8px; }
  .type-dot{ width:10px; height:10px; border-radius:999px; display:inline-block; }
  .count-badge{ margin-left:6px; font-size:.75rem; color:#1f2937; background:#fff; border:1px solid var(--border); border-radius:999px; padding:3px 8px; font-weight:700; }
  .chip{ border:1px solid var(--border); background:#fff; color:#374151; padding:6px 10px; border-radius:999px; font-size:.85rem; transition: all .2s ease; cursor: pointer; }
  .chip:hover{ transform: translateY(-1px); box-shadow: var(--shadow-1); }
  .chip.active{ background: var(--brand); color: white; border-color: var(--brand); }
  .ghost-btn{ background: transparent; border: 1px solid var(--border); padding: 6px; border-radius: 8px; cursor: pointer; transition: all .2s ease; display: flex; align-items: center; justify-content: center; }
  .ghost-btn:hover{ background: #f9fafb; }
  .muted{ color: var(--muted); font-size: .9rem; }
  .mtop{ margin-top: 8px; }

.components-grid{
  display: flex;
  flex-wrap: wrap;
  gap: 32px 40px;
  padding: 28px 16px 20px;
  align-items: flex-start;
  justify-content: center;
  max-width: calc(3 * 136px + 2 * 40px + 32px);
  margin: 0 auto;
}

.components-grid .circular-component{
  width: 136px;
  flex: 0 0 136px;
}

@media (max-width: 768px){
  .components-grid{
    max-width: calc(2 * 120px + 1 * 32px);
    gap: 24px 32px;
  }
  .components-grid .circular-component{
    width: 120px;
    flex: 0 0 120px;
  }
}

@media (max-width: 480px){
  .components-grid{
    max-width: calc(2 * 110px + 1 * 24px);
    gap: 20px 24px;
  }
  .components-grid .circular-component{
    width: 110px;
    flex: 0 0 110px;
  }
}

/* Hover pro */
.circular-component:hover:not(.is-locked){
  transform: translateY(-4px);
  box-shadow: var(--shadow-2);
  border-color: rgba(15, 95, 184, .22);
}

.circular-component.is-locked .component-name{ color:#64748b; }
.circular-component.is-locked .component-circle{ filter: grayscale(.55) contrast(.95) brightness(.98); }

@keyframes nextRingPulse{
  0%,100%{
    box-shadow:
      0 20px 60px rgba(34,197,94,.18),
      0 0 0 0 rgba(34,197,94,.18);
    transform: translateZ(0) scale(1);
  }
  50%{
    box-shadow:
      0 28px 86px rgba(34,197,94,.30),
      0 0 0 10px rgba(34,197,94,.10);
    transform: translateZ(0) scale(1.02);
  }
}

@keyframes nextShine{
  0%{ transform: translateX(-120%) rotate(20deg); opacity: 0; }
  15%{ opacity: .6; }
  55%{ opacity: .25; }
  100%{ transform: translateX(140%) rotate(20deg); opacity: 0; }
}

@keyframes nextBadgePop{
  0%,100%{ transform: translateY(0) scale(1); }
  50%{ transform: translateY(-2px) scale(1.04); }
}

/* halo externo (más visible) */
.circular-component.is-next .component-circle::before{
  content:"";
  position:absolute;
  inset:-10px;
  border-radius: 999px;
  pointer-events:none;
  background: transparent;
}

/* Texto del nombre: leve énfasis */
.circular-component.is-next .component-name{
  color:#065f46;
}

/* reduce motion */
@media (prefers-reduced-motion: reduce){
  .circular-component.is-next .component-circle,
}

@keyframes nextPing{
  0%{ transform: scale(.92); opacity: .0; }
  20%{ opacity: .35; }
  100%{ transform: scale(1.22); opacity: 0; }
}

.circular-component.is-next .component-circle .ring-centerBadge{
  position: relative;
}

.circular-component.is-next .component-circle .ring-centerBadge::before{
  content:"";
  position:absolute;
  inset:-8px;
  border-radius: 999px;
  border: 2px solid rgba(34,197,94,.35);
  animation: nextPing 1.65s ease-out infinite;
  pointer-events:none;
}


.circular-component.is-next{
  position: relative;
  border-color: transparent;
  filter: drop-shadow(0 0 12px rgba(56,189,248,.35));
}
/* halo controlado, no ensucia el box-shadow */
.circular-component.is-next::before{
  content:"";
  position:absolute;
  inset:-8px;
  border-radius: 999px;
  pointer-events:none;
  background: transparent;
  animation: none;
}

/* hover mantiene tu sombra pro sin pelear */
.circular-component.is-next:hover:not(.is-locked){
  border-color: rgba(34,197,94,.55);
}

/* reduce motion */
@media (prefers-reduced-motion: reduce){
  .circular-component.is-next::before{ animation:none !important; }
}


/* Si el usuario tiene reduce motion, no animar */
@media (prefers-reduced-motion: reduce){
  .circular-component.is-next,
  .circular-component.is-next::after{
    animation: none !important;
  }
}


.comp-badges{
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  display: flex;
  gap: 6px;
  align-items: center;
  white-space: nowrap;
}

.comp-badge{
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: .68rem;
  letter-spacing: .1px;
  border: 1px solid #bae6fd;
  background: #e0f2fe;
  color: #0284c7;
  box-shadow: 0 2px 8px rgba(2,6,23,.08);
  white-space: nowrap;
}

.comp-badge.next{
  border-color: #bae6fd;
  background: #e0f2fe;
  color: #0284c7;
}

.comp-badge.lock{
  border-color: rgba(148,163,184,.35);
  background: rgba(248,250,252,.92);
  color: #475569;
}

.comp-badge.done{
  border-color: rgba(16,185,129,.28);
  background: rgba(236,253,245,.92);
  color: #065f46;
}
/* Remueve el lock overlay viejo si existe */
.lock-overlay{ display:none !important; }

/* Nombre */
.component-info{ text-align:center; max-width: 200px; }
.component-name{
  margin: 14px 0 4px;
  color: #1e293b;
  font-weight: 900;
  font-size: .88rem;
  line-height: 1.3;
  letter-spacing: .1px;
  text-align: center;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  transition: color .18s ease;
}

.circular-component:hover:not(.is-locked) .component-name{
  color: #2563eb;
}

/* Tags: más sobrias */
.tags-row{ display:flex; gap:6px; justify-content:center; flex-wrap:wrap; }
.pill{
  border:1px solid rgba(15,23,42,.10);
  background:#fff;
  color:#475569;
  font-size:.75rem;
  padding:4px 8px;
  border-radius:999px;
  font-weight:800;
}
.pill.win{ border-color: rgba(16,185,129,.25); background: #ecfdf5; color:#065f46; }
.pill.lock{ border-color: rgba(148,163,184,.35); background: #f8fafc; color:#475569; }


    .test-gate{
    position: relative;
    overflow: hidden;
    margin: 14px 0 16px;
    padding: 14px 16px;
    border-radius: 16px;
    background:
      radial-gradient(420px 140px at 0% 0%, rgba(59,130,246,.10), transparent 62%),
      linear-gradient(180deg, #ffffff, #f8fbff);
    border: 1px solid #cfe3ff;
    box-shadow:
      0 12px 28px rgba(37,99,235,.09),
      inset 0 1px 0 rgba(255,255,255,.75);
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:14px;
    flex-wrap: wrap;
  }

  .test-gate::before{
    content:"";
    position:absolute;
    inset:0 auto 0 0;
    width:4px;
    background: linear-gradient(180deg, #3b82f6, #1d4ed8);
  }

  .test-gate.is-pending{
    border-color:#bfdbfe;
    background:
      radial-gradient(420px 140px at 0% 0%, rgba(59,130,246,.12), transparent 62%),
      linear-gradient(180deg, #ffffff, #f8fbff);
  }

  .test-gate.is-progress{
    border-color:#c7d2fe;
    background:
      radial-gradient(420px 140px at 0% 0%, rgba(99,102,241,.11), transparent 62%),
      linear-gradient(180deg, #ffffff, #f8faff);
  }

  .test-gate.is-locked{
    border-color:#fdba74;
    background:
      radial-gradient(420px 140px at 0% 0%, rgba(249,115,22,.12), transparent 62%),
      linear-gradient(180deg, #ffffff, #fffaf5);
  }

  .test-gate.is-locked::before{
    background: linear-gradient(180deg, #f97316, #ea580c);
  }

  .test-gate.is-approved{
    border-color:#bbf7d0;
    background:
      radial-gradient(420px 140px at 0% 0%, rgba(34,197,94,.10), transparent 62%),
      linear-gradient(180deg, #ffffff, #f7fff9);
  }

  .test-gate.is-approved::before{
    background: linear-gradient(180deg, #22c55e, #16a34a);
  }

  .tg-left{
    display:flex;
    align-items:flex-start;
    gap:12px;
    min-width:260px;
    flex: 1 1 360px;
  }

  .tg-ico{
    width:40px;
    height:40px;
    border-radius:12px;
    background: linear-gradient(135deg, #dbeafe, #eff6ff);
    color:#1d4ed8;
    display:flex;
    align-items:center;
    justify-content:center;
    border:1px solid #bfdbfe;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.7);
    flex: 0 0 auto;
  }

  .test-gate.is-approved .tg-ico{
    background: linear-gradient(135deg, #dcfce7, #f0fdf4);
    color:#15803d;
    border-color:#bbf7d0;
  }

  .tg-txt{
    display:flex;
    flex-direction:column;
    gap:4px;
    min-width:0;
  }

  .tg-kicker{
    font-size:.72rem;
    font-weight:900;
    letter-spacing:.08em;
    text-transform:uppercase;
    color:#2563eb;
  }

  .test-gate.is-approved .tg-kicker{
    color:#15803d;
  }

  .tg-titleRow{
    display:flex;
    align-items:center;
    gap:8px;
    flex-wrap:wrap;
  }

  .tg-title{
    color:#0f172a;
    font-size:1rem;
    font-weight:900;
    line-height:1.2;
  }

  .tg-required{
    display:inline-flex;
    align-items:center;
    gap:6px;
    padding:5px 9px;
    border-radius:999px;
    background:#eff6ff;
    border:1px solid #bfdbfe;
    color:#1d4ed8;
    font-size:.72rem;
    font-weight:800;
    white-space:nowrap;
  }

  .tg-sub{
    color:#475569;
    font-size:.92rem;
    line-height:1.45;
  }

  .tg-risk-note{
    margin-top:4px;
    color:#9a3412;
    font-size:.84rem;
    line-height:1.45;
    font-weight:700;
  }

  .tg-right{
    display:flex;
    align-items:center;
    justify-content:flex-end;
    gap:10px;
    flex-wrap:wrap;
    flex: 1 1 280px;
  }

  .tg-btn{
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  font-weight: 900;
  letter-spacing: .2px;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 11px 16px;
  min-height: 46px;
  border: 1px solid transparent;
  color: #fff;
  transition:
    transform .16s ease,
    box-shadow .22s ease,
    filter .22s ease,
    background .22s ease,
    border-color .22s ease;
}

.tg-btn::after{
  content:"";
  position:absolute;
  inset:0;
  background: linear-gradient(
    120deg,
    transparent 0%,
    rgba(255,255,255,.10) 35%,
    rgba(255,255,255,.22) 50%,
    transparent 65%
  );
  transform: translateX(-130%);
  transition: transform .7s ease;
  pointer-events:none;
}

.tg-btn:hover{
  transform: translateY(-1px);
  filter: brightness(1.04);
}

.tg-btn:hover::after{
  transform: translateX(130%);
}

.tg-btn:active{
  transform: translateY(0);
}

.tg-btn svg{
  transition: transform .18s ease;
}

.tg-btn:hover svg{
  transform: translateX(1px) scale(1.04);
}

.tg-btn.primary{
  border-color: #1d4ed8;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 55%, #1d4ed8 100%);
  box-shadow:
    0 12px 24px rgba(37,99,235,.18),
    inset 0 1px 0 rgba(255,255,255,.30);
}

.tg-btn.continue-test{
  border-color: #1e40af;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 58%, #1e40af 100%);
  box-shadow:
    0 14px 28px rgba(37,99,235,.22),
    0 0 0 0 rgba(59,130,246,.16),
    inset 0 1px 0 rgba(255,255,255,.30);
  animation: tgContinuePulse 2.8s ease-in-out infinite;
}

.tg-btn.continue-test:hover{
  box-shadow:
    0 18px 36px rgba(37,99,235,.28),
    0 0 0 6px rgba(59,130,246,.08),
    inset 0 1px 0 rgba(255,255,255,.34);
  animation-play-state: paused;
}

.tg-btn.cta-test{
  border-color: #6d28d9;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 55%, #6d28d9 100%);
  box-shadow:
    0 16px 32px rgba(124,58,237,.24),
    0 0 0 0 rgba(139,92,246,.16),
    inset 0 1px 0 rgba(255,255,255,.28);
  animation: tgCtaPulse 2.6s ease-in-out infinite;
}

.tg-btn.cta-test:hover{
  box-shadow:
    0 20px 40px rgba(124,58,237,.32),
    0 0 0 7px rgba(139,92,246,.09),
    inset 0 1px 0 rgba(255,255,255,.32);
  animation-play-state: paused;
}

.tg-btn:focus-visible{
  outline: 3px solid rgba(165,180,252,.8);
  outline-offset: 2px;
}

.tg-btn.cta-test:focus-visible{
  outline-color: rgba(196,181,253,.7);
}

.tg-btn.continue-test:focus-visible{
  outline-color: rgba(147,197,253,.7);
}

@keyframes tgContinuePulse{
  0%, 100%{
    box-shadow:
      0 14px 28px rgba(37,99,235,.22),
      0 0 0 0 rgba(59,130,246,.16),
      inset 0 1px 0 rgba(255,255,255,.30);
  }
  50%{
    box-shadow:
      0 18px 36px rgba(37,99,235,.27),
      0 0 0 6px rgba(59,130,246,.07),
      inset 0 1px 0 rgba(255,255,255,.34);
  }
}

@keyframes tgCtaPulse{
  0%, 100%{
    box-shadow:
      0 16px 32px rgba(124,58,237,.24),
      0 0 0 0 rgba(139,92,246,.16),
      inset 0 1px 0 rgba(255,255,255,.28);
  }
  50%{
    box-shadow:
      0 20px 40px rgba(124,58,237,.30),
      0 0 0 7px rgba(139,92,246,.08),
      inset 0 1px 0 rgba(255,255,255,.32);
  }
}

@media (max-width: 640px){
  .tg-right{
    justify-content:flex-start;
  }

  .tg-btn{
    width:100%;
  }
}

@media (prefers-reduced-motion: reduce){
  .tg-btn::after{
    display:none;
  }

  .tg-btn.continue-test,
  .tg-btn.cta-test{
    animation:none;
  }

  .tg-btn:hover svg{
    transform:none;
  }
}

  .tg-btn.secondary{
    border:1px solid #cbd5e1;
    background:#ffffff;
    color:#0f172a;
    box-shadow:
      0 10px 22px rgba(15,23,42,.08),
      inset 0 1px 0 rgba(255,255,255,.6);
  }

  .tg-btn.secondary:hover{
    filter:none;
    background:#f8fafc;
  }

  .tg-tries{
    color:#334155;
    background:#eef2ff;
    border:1px dashed #c7d2fe;
    padding:6px 10px;
    border-radius:999px;
    font-weight:700;
  }

  .tg-badge{
    color:#92400e;
    background:#fffbeb;
    border:1px solid #fde68a;
    padding:6px 10px;
    border-radius:999px;
    font-weight:700;
  }

  .tg-badge.ok{
    color:#065f46;
    background:#ecfdf5;
    border-color:#a7f3d0;
  }

  .tg-approved{
    font-weight:900;
    color:#065f46;
    background:#ecfdf5;
    border:1px solid #a7f3d0;
    padding:6px 10px;
    border-radius:999px;
  }

  .tg-locked-pill{
    font-weight:900;
    color:#9a3412;
    background:#fff7ed;
    border:1px solid #fdba74;
    padding:6px 10px;
    border-radius:999px;
  }

  .tg-penalty-note{
    display:none;
    margin:-4px 0 14px;
    padding:12px 14px;
    border-radius:14px;
    border:1px solid #fed7aa;
    background:linear-gradient(180deg,#fff7ed,#fffbeb);
    color:#9a3412;
    font-size:.92rem;
    line-height:1.45;
    box-shadow:0 10px 24px rgba(154,52,18,.08);
  }

  @media (max-width: 640px){
    .tg-right{
      justify-content:flex-start;
    }

    .tg-btn{
      width:100%;
    }
  }

  .ltm-backdrop{
    position:fixed; inset:0; z-index: 9999;
    background: rgba(2, 6, 23, .5);
    display:grid; place-items:center; padding: 16px;
  }
  .ltm-card{
    width:min(560px, 100%); border-radius: 16px; overflow:hidden;
    background: #ffffff;
    box-shadow: 0 30px 80px rgba(0,0,0,.35);
    border: 1px solid #e5e7eb;
    display:flex; flex-direction:column; gap:12px; padding:18px;
  }
  .ltm-card-warn{
    border-color:#fdba74;
    background:linear-gradient(180deg,#fffaf5,#ffffff);
  }
  .ltm-head{ display:flex; gap:12px; align-items:flex-start; }
  .ltm-ico{ width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; background:#e0f2fe; color:#075985; border:1px solid #bae6fd; }
  .ltm-ico-warn{ background:#fff7ed; color:#c2410c; border-color:#fdba74; }
  .ltm-sub{ color:#475569; margin:4px 0 0; }
  .ltm-list{ margin: 4px 0 0 0; padding-left: 18px; color:#475569; }
  .ltm-list li{ margin: 4px 0; }
  .ltm-check{ display:flex; align-items:center; gap:8px; margin-top:8px; user-select:none; color:#374151; }
  .ltm-actions{ display:flex; align-items:center; gap:10px; justify-content:flex-end; margin-top:6px; }
  .ltm-btn{ border:1px solid #e5e7eb; background:#fff; color:#111827; padding:8px 12px; border-radius:10px; font-weight:700; cursor:pointer; }
  .ltm-btn.primary{ border-color:#1d4ed8; background:#1d4ed8; color:#fff; }
  .ltm-btn.ghost:hover{ background:#f9fafb; }
  .ltm-btn.primary:hover{ filter:brightness(1.05); transform: translateY(-1px); }

  .hero-title-row{ display:flex; align-items:center; gap:12px; flex-wrap:wrap; justify-content:space-between; }
  .hero.compact{ grid-template-rows: auto; grid-template-areas: "title stats"; }
  .hero.compact .hero-extra-area{ display:none; }

  .task-topbar{ display:flex; align-items:center; margin-bottom: 12px; }
  .back-button{
    display:inline-flex; align-items:center; gap:10px;
    padding:10px 14px;
    border-radius:12px;
    border:1px solid rgba(0,0,0,.08);
    background:#fff;
    cursor:pointer;
    font-weight:800;
  }
  .back-button:hover{ transform: translateY(-1px); }
  .hide-xs{ display:inline; }
  @media (max-width: 480px){ .hide-xs{ display:none; } }

  .empty-mini{ padding: 12px; color:#6b7280; }

  /* ===== Component cards: estados pro ===== */
/* ===== Component cards: SIN CUADRO (solo contenido) ===== */
.circular-component{
  appearance:none;
  position: relative;
  cursor:pointer;

  /* ✅ sin tarjeta */
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;

  /* layout */
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:10px;

  padding: 6px 4px;         /* más compacto */
  border-radius: 18px;       /* solo por hit-area */
  transition: transform .18s ease, filter .18s ease;
}

.circular-component:hover:not(.is-locked){
  transform: translateY(-3px);
}


.circular-component.is-active{
  border-color: color-mix(in srgb, var(--accent) 28%, #e2e8f0);
}

.circular-component.is-active:hover{
  transform: translateY(-5px);
  box-shadow: 0 18px 44px rgba(2, 6, 23, .12);
  background:
    radial-gradient(140px 140px at 20% 15%, color-mix(in srgb, var(--accent) 18%, transparent), transparent 60%),
    radial-gradient(140px 140px at 80% 30%, rgba(59,130,246,.10), transparent 60%),
    linear-gradient(180deg, rgba(255,255,255,.95), rgba(248,250,252,.95));
}

.circular-component.is-completed{
  border-color: rgba(34,197,94,.25);
  background:
    radial-gradient(140px 140px at 30% 20%, rgba(34,197,94,.12), transparent 60%),
    linear-gradient(180deg, rgba(255,255,255,.94), rgba(246,255,250,.92));
}

.circular-component.is-locked{
  cursor:not-allowed;
  border-style: dashed;
  border-color: rgba(148,163,184,.55);
  background:
    linear-gradient(180deg, rgba(248,250,252,.92), rgba(241,245,249,.92));
  box-shadow: 0 8px 18px rgba(2,6,23,.06);
  filter: grayscale(.22);
}

/* === SOLO CIRCULO (sin cuadro alrededor) === */
/* === SOLO CIRCULO (sin cuadro alrededor) === */
.component-circle{
  position: relative;
  width: 112px;
  height: 112px;
  padding: 0;
  border-radius: 999px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (min-width: 1024px){
  .component-circle{
    width: 144px;
    height: 144px;
  }
}

.circular-component.is-active .component-circle{
  outline: 1px solid color-mix(in srgb, var(--accent) 20%, transparent);
  box-shadow: 0 14px 30px rgba(2,6,23,.08);
}

.status-badge{
  position:absolute;
  top: 10px;
  left: 10px;
  z-index: 5;
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: .72rem;
  letter-spacing: .2px;
  border: 1px solid rgba(226,232,240,.9);
  background: rgba(255,255,255,.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 10px 24px rgba(2,6,23,.10);
}

.status-badge.active{
  border-color: color-mix(in srgb, var(--accent) 28%, #e2e8f0);
  color: color-mix(in srgb, var(--accent) 82%, #0f172a);
}

.status-badge.done{
  border-color: rgba(34,197,94,.28);
  color:#047857;
  background: rgba(236,253,245,.92);
}

.status-badge.locked{
  border-color: rgba(148,163,184,.5);
  color:#334155;
  background: rgba(248,250,252,.92);
}

.ring-centerBadge{
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight: 950;
  font-size: 18px;
  box-shadow: 0 12px 28px rgba(2,6,23,.18);
  border: 1px solid rgba(226,232,240,.95);
  background: rgba(255,255,255,.92);
}

.ring-centerBadge.ring-ok{
  border-color: rgba(34,197,94,.30);
  color:#047857;
  background: rgba(236,253,245,.92);
}

.ring-centerBadge.ring-lock{
  border-color: rgba(148,163,184,.55);
  color:#334155;
  background: rgba(248,250,252,.92);
}
/* =======================
   TOUR (Tutorial Overlay) - FINAL (SOLO CIRCULO)
======================= */

/* Root del tour */
.tour-root{
  position: fixed;
  inset: 0;
  z-index: 99999;
  pointer-events: none; /* deja pasar click por el hueco */
}

/* ✅ Permite click SOLO en la tarjeta y sus controles */
.tour-card{
  pointer-events: auto;
}

.tour-actions,
.tour-btn{
  pointer-events: auto;
}

/* 4 overlays alrededor del hueco (oscurece + blur) */
.tour-dim{
  position: fixed;
  background: rgba(2, 6, 23, .35); /* ✅ Menos oscuro: de .55 a .35 */
  backdrop-filter: blur(4px) saturate(1.05); /* ✅ Menos blur: de 7px a 4px */
  -webkit-backdrop-filter: blur(4px) saturate(1.05);
  animation: tourFade .18s ease-out both;
  pointer-events: auto;
}

@keyframes tourFade{
  from{ opacity: 0; }
  to{ opacity: 1; }
}

.tour-hole{
  position: fixed;
  z-index: 100000;
  border: 1px solid rgba(255,255,255,.30);
  box-shadow:
    0 0 0 9999px rgba(2, 6, 23, .22),
    0 0 0 6px rgba(255,255,255,.08),
    0 22px 70px rgba(0,0,0,.45);
  animation: holePulse 1.2s ease-in-out infinite;
  transition:
    top .22s ease,
    left .22s ease,
    width .22s ease,
    height .22s ease,
    border-color .22s ease,
    border-radius .22s ease,
    box-shadow .22s ease;
  
  /* ✅ Por defecto no interfiere */
  pointer-events: none;
}

/* ✅ Cuando es clickable, permite eventos */
.tour-hole.clickable {
  pointer-events: auto;
  cursor: pointer; /* ✅ AÑADIR esta línea */
}

@keyframes holePulse{
  0%,100%{
    border-color: rgba(255,255,255,.28);
    box-shadow:
      0 0 0 9999px rgba(2, 6, 23, .35),
      0 0 0 6px rgba(255,255,255,.08),
      0 22px 70px rgba(0,0,0,.45);
  }
  50%{
    border-color: rgba(255,255,255,.55);
    box-shadow:
      0 0 0 9999px rgba(2, 6, 23, .35),
      0 0 0 10px rgba(255,255,255,.12),
      0 22px 70px rgba(0,0,0,.45);
  }
}


.tour-card{
  position: fixed;
  width: min(560px, calc(100vw - 24px));
  background: #ffffff;                 /* ✅ sólido */
  opacity: 1;
  border: 1px solid rgba(226,232,240,.98);
  border-radius: 18px;
  box-shadow: 0 34px 120px rgba(0,0,0,.45);
  z-index: 100002;                     /* ✅ encima del dim/hole */
  padding: 14px 14px 12px;
  pointer-events: auto;
}

.tour-card:not(.has-target){
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.tour-card.has-target{
  transform: none;
}


@keyframes tourPop{
  from{ transform: translateY(8px) scale(.99); opacity: 0; }
  to{ transform: translateY(0) scale(1); opacity: 1; }
}

.tour-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.tour-title{
  color:#0f172a;
  font-weight: 950;
  letter-spacing:.2px;
  font-size: 1.02rem;
}

.tour-step{
  font-weight: 900;
  font-size: .78rem;
  padding: 6px 10px;
  border-radius: 999px;
  background:#eef2ff;
  border:1px solid #c7d2fe;
  color:#3730a3;
  flex: 0 0 auto;
}

/* Texto en párrafos (sin huecos gigantes) */
.tour-text{
  margin-top: 8px;
  color:#334155;
  line-height:1.55;
  font-size:.95rem;
}
.tour-p{ margin: 0 0 8px 0; }
.tour-p:last-child{ margin-bottom: 0; }

.tour-warning{
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-weight: 900;
}

.tour-actions{
  margin-top: 12px;
  display:flex;
  align-items:center;
  justify-content:flex-end;
  gap: 10px;
}

.tour-btn{
  border:none;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 950;
  cursor:pointer;
  transition: transform .12s ease, filter .12s ease, background .12s ease;
}

.tour-btn.primary{
  background: linear-gradient(135deg,#2563eb,#1d4ed8);
  color:#fff;
  border: 1px solid #1d4ed8;
  box-shadow: 0 14px 34px rgba(37,99,235,.22), inset 0 1px 0 rgba(255,255,255,.35);
}
.tour-btn.primary:hover{ transform: translateY(-1px); filter: brightness(1.05); }

.tour-btn.ghost{
  background:#fff;
  border:1px solid #e5e7eb;
  color:#0f172a;
}
.tour-btn.ghost:hover{ background:#f8fafc; transform: translateY(-1px); }

`;

/* =========================================================
   Presentacionales (estables)
========================================================= */
const LinearProgress = React.memo(function LinearProgress({
  value,
  color,
  height = 10,
  label,
}) {
  return (
    <div className="lp-wrap">
      <div className="lp-track" style={{ height }}>
        <div
          className="lp-fill"
          style={{ width: `${value}%`, background: color }}
        />
      </div>
      {label ? <span className="lp-label">{label}</span> : null}
    </div>
  );
});

function CircularComponent({
  component,
  accentColor,
  showDetails = false,
  calculateRealProgress,
  onDownloadLevelReport,
  downloadingLevelId,
  canDownloadReport,
  onTestStatus,
  isCompletedItem,
  isUnlockedItem,
  handleComponentClick,
  innerRef,
  domId,
  isSequentialModule = true,
}) {
  const progress = toNumber(calculateRealProgress(component), 0);
  const completed = isCompletedItem(component);
  const unlocked = isUnlockedItem(component);
  const isNext = unlocked && !completed && progress < 100;

  // ✅ hooks directamente en el componente, no en un callback
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setAnimPct(progress), 100);
    return () => clearTimeout(t);
  }, [progress]);

  const isCompleted = completed;
  const isCurrent = unlocked && !completed;

  return (
    <button
      id={domId}
      ref={innerRef}
      className={`circular-component ${completed ? "is-completed" : ""} ${!unlocked ? "is-locked" : ""} ${isNext ? "is-next" : ""}`}
      style={{ "--accent": accentColor || "#0f5fb8" }}
      onClick={() => unlocked && handleComponentClick(component)}
      disabled={!unlocked}
      title={unlocked ? "Abrir componente" : "Bloqueado"}
      aria-label={`${component.nombre} ${progress}%`}
      type="button"
    >
      <div className="component-circle">

        {/* Badge "Iniciar" — encima del círculo */}
        {isNext && (
          <div style={{
            position: 'absolute',
            top: '0px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            whiteSpace: 'nowrap',
          }}>
            <span className="bg-[#e0f2fe] text-[#0284c7] text-[10px] font-extrabold px-3 py-1 rounded-full border border-white shadow-sm">
              {isSequentialModule ? 'Iniciar' : 'Desbloqueado'}
            </span>
          </div>
        )}

        {/* Check completado — esquina superior izquierda */}
        {completed && (
          <div style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            zIndex: 10,
          }}>
            <div className="bg-[#dcfce7] rounded-full p-1.5 border-2 border-white shadow-sm">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </div>
        )}

        {/* Candado — encima centrado */}
        {!unlocked && (
          <div style={{
            position: 'absolute',
            top: '-2px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}>
            <div className="bg-[#f8fafc] rounded-full p-1.5 border-2 border-white shadow-sm">
              <Lock size={12} className="text-gray-400" />
            </div>
          </div>
        )}

        {/* Anillo SVG */}
        <div className="relative w-28 h-28 lg:w-36 lg:h-36 rounded-full flex items-center justify-center bg-white shadow-[0_0_20px_rgba(0,0,0,0.03)]">
          <svg className="absolute inset-0 w-full h-full transform -rotate-90">
            <circle cx="50%" cy="50%" r="42%" fill="none" className="stroke-[#f1f5f9]" strokeWidth="10" />
            <circle
              cx="50%" cy="50%" r="42%" fill="none"
              className={`transition-all duration-1000 ease-out ${isCompleted ? 'stroke-[#22c55e]' :
                isCurrent ? 'stroke-[#38bdf8]' :
                  'stroke-transparent'
                }`}
              strokeWidth="10"
              pathLength="100"
              strokeDasharray="100"
              strokeDashoffset={Math.max(0, 100 - animPct)}
              strokeLinecap="round"
            />
          </svg>
          <div className="flex flex-col items-center justify-center z-10">
            <span className={`text-2xl lg:text-3xl font-black ${isCompleted ? 'text-[#16a34a]' :
              isCurrent ? 'text-[#0284c7]' :
                'text-gray-400'
              }`}>{Math.round(animPct)}%</span>
            <span className={`text-[9px] font-bold mt-0.5 uppercase ${isCompleted ? 'text-[#9ca3af]' :
              isCurrent ? 'text-[#38bdf8]' :
                'text-gray-400'
              }`}>
              {isCompleted ? 'Finalizado' : isCurrent ? 'En curso' : 'Cerrado'}
            </span>
          </div>
        </div>

      </div>

      <div className="component-info">
        <h4 className="component-name" title={component.nombre}>
          {component.nombre}
        </h4>
      </div>
    </button>
  );
}

const ModuleSection = React.memo(function ModuleSection({
  mod,
  codigoCurso,
  calculateRealProgress,
  onDownloadLevelReport,
  downloadingLevelId,
  canDownloadReport,
  onTestStatus,
  filterComponents,
  typeStyle,
  navigate,
  showDetails = false,
  isCompletedItem,
  isUnlockedItem,
  handleComponentClick,
  registerRef,
  location,
}) {
  const progress = toNumber(calculateRealProgress(mod), 0);
  const t = typeStyle[mod.tipo] || {};
  const unlocked = isUnlockedItem(mod);
  const compsAll = (mod.children || []).filter((c) => c.tipo === "componente");
  const comps = filterComponents(compsAll);
  // 🔐 Bandera secuencia_hijos / secuencial_hijos a boolean
  const rawSeq = mod.secuencia_hijos ?? mod.secuencial_hijos;
  const isSequentialModule = flagToBoolean(rawSeq, true);

  return (
    <section
      className={`module-section ${!unlocked ? "is-locked" : ""}`}
      data-tour="first-module"
      style={!unlocked ? { background: "#f8fafc", opacity: 0.72 } : { background: t.bg }}
    >
      <header className="module-header">
        <div className="module-title">
          <span className="type-dot" style={{ background: t.color }} />
          <h3 style={{ color: t.color }}>{mod.nombre}</h3>
        </div>

        <div className="module-toolbar">
          <div className="module-progress">
            <LinearProgress value={progress} color={t.color} height={8} />
            <span className="progress-text">{progress}%</span>
          </div>

          <button
            className="chip"
            disabled={!unlocked}
            title="Ver recursos del módulo"
            onClick={() =>
              unlocked && navigate(`/curso/${codigoCurso}/modulo/${mod.id}/recursos`, {
                state: { from: location.pathname },
              })
            }
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <FolderOpen size={16} /> Recursos
            {typeof mod.recursos_count === "number" && (
              <span className="count-badge">{mod.recursos_count}</span>
            )}
          </button>
        </div>
      </header>

      {mod.descripcion && showDetails ? (
        <p className="muted mtop">{mod.descripcion}</p>
      ) : null}

      <div
        className={
          "components-grid " +
          (comps.length === 1 ? "one" : "few")
        }
      >
        {!unlocked ? (
          <div className="empty-mini">Modulo bloqueado</div>
        ) : comps.length ? (
          comps.map((c) => (
            <CircularComponent
              key={c.id}
              component={c}
              accentColor={t.color} // ✅ AÑADIR
              showDetails={showDetails}
              calculateRealProgress={calculateRealProgress}
              isCompletedItem={isCompletedItem}
              isUnlockedItem={isUnlockedItem}
              handleComponentClick={handleComponentClick}
              domId={`comp-${c.id}`}
              innerRef={(el) => registerRef(c.id, el)}
              isSequentialModule={isSequentialModule}
            />
          ))
        ) : (
          <div className="empty-mini">No hay componentes para este filtro</div>
        )}
      </div>
    </section>
  );
});

/* =========================================================
   Persistencia toggles (localStorage)
========================================================= */
const useStickyToggle = (key, defaultOpen = true) => {
  const [open, setOpen] = React.useState(defaultOpen);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === "0" || raw === "1") setOpen(raw === "1");
    } catch { }
  }, [key]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(key, next ? "1" : "0");
      } catch { }
      return next;
    });
  }, [key]);

  return [open, toggle];
};

/* =========================================================
   Test gate + Level Section
========================================================= */
const STRUCTURAL_TYPES = new Set(["nivel", "modulo", "componente", "tarea"]);

function getStructuralChildren(node) {
  return (node?.children || []).filter((child) =>
    STRUCTURAL_TYPES.has(child?.tipo),
  );
}

function isStructuralBranchCompleted(node, isCompletedItem, calculateRealProgress) {
  if (!node) return false;

  const structuralChildren = getStructuralChildren(node);
  if (!structuralChildren.length) {
    const progress = toNumber(calculateRealProgress?.(node), 0);
    return isCompletedItem?.(node) || progress >= 100;
  }

  return structuralChildren.every((child) =>
    isStructuralBranchCompleted(child, isCompletedItem, calculateRealProgress),
  );
}

function LevelTestGate({
  nivel,
  codigoCurso,
  onStatus,
  onDownloadLevelReport,
  downloadingLevelId,
  canDownloadReport,
  levelStructureReady = false,
  levelCompleted = false,
  levelProgress = 0,
  levelStructureProgress = 0,
  refreshKey,
}) {
  const navigate = useNavigate();
  const { getLevelTestStatus, startLevelTest } = useFuncion();

  const getLevelTestStatusRef = React.useRef(getLevelTestStatus);
  const startLevelTestRef = React.useRef(startLevelTest);

  React.useEffect(() => {
    getLevelTestStatusRef.current = getLevelTestStatus;
    startLevelTestRef.current = startLevelTest;
  }, [getLevelTestStatus, startLevelTest]);

  const [state, setState] = React.useState({
    loading: true,
    status: "loading",
  });

  const normalizeTestGateState = React.useCallback((raw = {}) => {
    const testId = raw?.testId ?? raw?.test_id ?? raw?.id ?? null;
    const presentacionId =
      raw?.presentacionId ?? raw?.presentacion_id ?? raw?.presentationId ?? null;

    const intentosMax = asNum(
      raw?.intentosMax ??
      raw?.intentos_maximos ??
      raw?.maxAttempts ??
      raw?.attemptsMax,
      null,
    );

    const usados = asNum(
      raw?.usados ??
      raw?.used ??
      raw?.usadosFinales ??
      raw?.intentos_usados ??
      raw?.usedAttempts,
      0,
    );

    const restantesRaw = raw?.restantes ?? raw?.remaining ?? raw?.attempts_remaining;
    const restantes =
      restantesRaw == null
        ? (intentosMax == null ? null : Math.max(intentosMax - usados, 0))
        : asNum(restantesRaw, 0);

    const intentosConPremioRestantes = asNum(
      raw?.intentosConPremioRestantes ??
      raw?.intentos_con_premio_restantes ??
      raw?.rewardAttemptsRemaining ??
      restantes,
      restantes ?? 0,
    );

    const normalizedStatus = String(raw?.status || "no_test").toLowerCase();

    return {
      ...raw,
      loading: false,
      testId,
      presentacionId,
      status: normalizedStatus,
      intentosMax,
      usados,
      restantes,
      intentosConPremioRestantes,
      penaltyNotice: raw?.penaltyNotice ?? raw?.penalty_notice ?? null,
    };
  }, []);

  const [showInfo, setShowInfo] = React.useState(false);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);
  const [showPenaltyModal, setShowPenaltyModal] = React.useState(false);
  const storageKey = `hideLevelTestInfo:${codigoCurso}:${nivel.id}`;
  const blockedStatuses = React.useMemo(() => [
    "locked",
    "blocked",
    "bloqueado",
    "attempts_exhausted",
    "intentos_agotados",
    "no_attempts",
    "agotado",
  ], []);

  // ✅ REF DE CONTROL: Evita peticiones duplicadas o bucles infinitos
  const isFetching = React.useRef(false);

  const onStatusRef = React.useRef(onStatus);

  const statusRunRef = React.useRef(0);

  React.useEffect(() => {
    onStatusRef.current = onStatus;
  }, [onStatus]);

  React.useEffect(() => {
    isFetching.current = false;
    setState({
      loading: true,
      status: "loading",
    });
  }, [nivel.id, refreshKey]);

  React.useEffect(() => {
    let alive = true;
    const runId = ++statusRunRef.current;

    if (isFetching.current) return;

    (async () => {
      isFetching.current = true;
      try {
        const data = await getLevelTestStatusRef.current?.(nivel.id);
        if (!alive || statusRunRef.current !== runId) return;

        // 🔧 Normalización robusta (tu versión mejorada)
        const testId = data?.testId ?? data?.test_id ?? data?.id ?? null;

        const isActive = asBool(
          data?.activo ??
          data?.is_active ??
          data?.enabled ??
          data?.habilitado ??
          data?.status_active,
          true
        );

        const questionsCount = asNum(
          data?.questionsCount ??
          data?.preguntas_count ??
          data?.total_preguntas ??
          data?.totalQuestions ??
          data?.items_count,
          1
        );

        const hasQuestions = questionsCount > 0;
        const hasConfiguredTest = !!testId;

        if (!hasConfiguredTest) {
          const nextState = normalizeTestGateState(data);
          setState(nextState);
          try {
            onStatusRef.current?.(nextState);
          } catch { }
          return;
        }

        const nextState = normalizeTestGateState({
          ...data,
          testId,
          activo: isActive,
          preguntasCount: questionsCount,
          hasQuestions,
        });

        setState(nextState);

        // ✅ Mantener compatibilidad con ref (evita re-renders)
        try {
          onStatusRef.current?.(nextState);
        } catch { }

        // 🔔 Modal informativo
        const shouldSilence = (() => {
          try {
            return localStorage.getItem(storageKey) === "1";
          } catch {
            return false;
          }
        })();

        const statusForModal = String(nextState.status || "").toLowerCase();
        const blockedForModal = blockedStatuses.includes(statusForModal);
        const noAttemptsForModal =
          statusForModal !== "in_progress" &&
          statusForModal !== "approved" &&
          nextState.restantes !== null &&
          nextState.restantes !== undefined &&
          Number(nextState.restantes) <= 0;

        if (!shouldSilence && !blockedForModal && !noAttemptsForModal && nextState.status !== "approved" && nextState.status !== "no_test") {
          setShowInfo(true);
        }

      } catch (err) {
        if (alive && statusRunRef.current === runId) {
          setState({ loading: false, error: true });
        }
      } finally {
        isFetching.current = false;
      }
    })();

    return () => { alive = false; };
  }, [nivel.id, storageKey, normalizeTestGateState, refreshKey, blockedStatuses]);

  const closeInfo = () => {
    if (dontShowAgain) {
      try { localStorage.setItem(storageKey, "1"); } catch { }
    }
    setShowInfo(false);
  };

  React.useEffect(() => {
    const createdAt = state?.penaltyNotice?.createdAt;
    if (!createdAt) {
      setShowPenaltyModal(false);
      return;
    }

    const seenKey = `seenLevelPenalty:${codigoCurso}:${nivel.id}:${createdAt}`;
    let seen = false;
    try {
      seen = localStorage.getItem(seenKey) === "1";
    } catch {
      seen = false;
    }
    setShowPenaltyModal(!seen);
  }, [codigoCurso, nivel.id, state?.penaltyNotice]);

  const closePenaltyModal = React.useCallback(() => {
    const createdAt = state?.penaltyNotice?.createdAt;
    if (createdAt) {
      const seenKey = `seenLevelPenalty:${codigoCurso}:${nivel.id}:${createdAt}`;
      try { localStorage.setItem(seenKey, "1"); } catch { }
    }
    setShowPenaltyModal(false);
  }, [codigoCurso, nivel.id, state?.penaltyNotice]);

  const backendReady = asBool(
    state.structureCompleted ??
    state.enabledByProgressTable ??
    state.enabledByLevelProgress,
    false,
  );
  const isBlockedStatus = blockedStatuses.includes(String(state.status || "").toLowerCase());
  const hasNoAttemptsLeft =
    state.status !== "in_progress" &&
    state.status !== "approved" &&
    state.restantes !== null &&
    state.restantes !== undefined &&
    Number(state.restantes) <= 0;
  const canLaunchTest =
    !!state.testId &&
    !isBlockedStatus &&
    !hasNoAttemptsLeft &&
    (
      backendReady ||
      state.status === "enabled" ||
      state.status === "in_progress"
    );

  const buildCanLaunchTest = React.useCallback((candidateState = state) => {
    const candidateBackendReady = asBool(
      candidateState.structureCompleted ??
      candidateState.enabledByProgressTable ??
      candidateState.enabledByLevelProgress,
      false,
    );
    const candidateStatus = String(candidateState.status || "").toLowerCase();
    const candidateBlocked = blockedStatuses.includes(candidateStatus);
    const candidateNoAttempts =
      candidateStatus !== "in_progress" &&
      candidateStatus !== "approved" &&
      candidateState.restantes !== null &&
      candidateState.restantes !== undefined &&
      Number(candidateState.restantes) <= 0;

    return !!candidateState.testId && !candidateBlocked && !candidateNoAttempts && (
      candidateBackendReady ||
      candidateStatus === "enabled" ||
      candidateStatus === "in_progress"
    );
  }, [state, blockedStatuses]);

  const goToTest = async () => {
    try {
      if (state.status === "in_progress") {
        navigate(`/curso/${codigoCurso}/nivel/${nivel.id}/test/${state.presentacionId}`, {
          state: { openPreflight: true, resumeAttempt: true, nivelNombre: nivel.nombre },
        });
      } else {
        const freshData = await getLevelTestStatusRef.current?.(nivel.id);
        const freshState = normalizeTestGateState(freshData || {});
        setState(freshState);
        try {
          onStatusRef.current?.(freshState);
        } catch { }

        if (!buildCanLaunchTest(freshState)) {
          return;
        }

        const res = await startLevelTestRef.current?.(freshState.testId);
        navigate(
          `/curso/${codigoCurso}/nivel/${nivel.id}/test/${res.presentacionId}`,
          {
            state: {
              openPreflight: true,
              resumeAttempt: false,
              nivelNombre: nivel.nombre,
            },
          },
        );
      }
    } catch (err) {
      const msg = String(err?.message || "");
      if (
        msg.includes("agotaste los intentos") ||
        msg.includes("LEVEL_TEST_ATTEMPTS_EXHAUSTED")
      ) {
        setState((prev) => ({
          ...prev,
          status: "attempts_exhausted",
          restantes: 0,
          puedeIntentar: false,
          loading: false,
        }));
      } else {
        alert(msg || "No se pudo iniciar el test de nivel.");
      }
    } finally {
      closeInfo();
    }
  };

  if (state.loading || state.error) return null;

  const isApproved = state.status === "approved";
  const isInProgress = state.status === "in_progress";
  const isNoTestFinished = state.status === "no_test" && canDownloadReport;
  const isFallbackReady = !isApproved && !isNoTestFinished && canLaunchTest;
  const penaltyNotice = state.penaltyNotice || null;
  const penaltyPctConfigured = toNumber(
    state.penalizacionProgresoPct ??
    state.penalizacion_progreso_pct,
    100,
  );
  const hasPenalty = penaltyPctConfigured > 0;
  const showPenaltyReminder = hasPenalty && !isApproved && !isNoTestFinished && !isInProgress && !hasNoAttemptsLeft;
  const isLockedByPenalty = isBlockedStatus && !!penaltyNotice && hasPenalty;
  const isAttemptsExhausted = !isApproved && !isNoTestFinished && (hasNoAttemptsLeft || (isBlockedStatus && !isLockedByPenalty));
  const gateTitle = isApproved
    ? "Test del nivel aprobado"
    : isNoTestFinished
      ? "Nivel finalizado"
      : isLockedByPenalty
        ? "Debes recompletar el nivel"
        : isAttemptsExhausted
          ? "Intentos agotados"
          : isInProgress
            ? "Continua tu test"
            : "Rinde el test del nivel";
  const gateSubtitle = isApproved
    ? "Completado. Puedes revisar tu intento."
    : isLockedByPenalty
      ? "Tu progreso fue ajustado segun la configuracion del test. Completa nuevamente la estructura del nivel para volver a rendirlo."
      : isAttemptsExhausted
        ? "Ya usaste los intentos disponibles para este test. Puedes revisar el resultado o descargar el reporte."
        : "Debes aprobarlo para desbloquear el siguiente nivel.";
  if (!isFallbackReady && state.status === "no_test" && !canDownloadReport) return null;
  if (!isFallbackReady && state.status === "locked" && !canDownloadReport && !isLockedByPenalty && !isAttemptsExhausted) return null;

  const Attempts = () => (
    <span className="tg-tries">
      {state.restantes} / {state.intentosMax} intentos
    </span>
  );

  const CoinsBadge = () => (
    <span className={`tg-badge ${state.eligibleCoins ? "ok" : ""}`}>
      {state.eligibleCoins ? "Con UTMCoins" : "Sin UTMCoins"}
    </span>
  );

  const gateClass = isApproved || isNoTestFinished
    ? "test-gate is-approved"
    : (isLockedByPenalty || isAttemptsExhausted) ? "test-gate is-locked" : isInProgress ? "test-gate is-progress" : "test-gate is-pending";

  return (
    <>
      <div className={gateClass} role="region">
        <div className="tg-left">
          <div className="tg-ico"><Play size={18} /></div>
          <div className="tg-txt">
            <span className="tg-kicker">Evaluación del nivel</span>
            <div className="tg-titleRow">
              <strong className="tg-title">
                {gateTitle}
              </strong>
              {!isApproved && !isNoTestFinished && !isAttemptsExhausted && <span className="tg-required">Requerido</span>}
            </div>
            <span className="tg-sub">
              {gateSubtitle}
            </span>
            {showPenaltyReminder ? (
              <span className="tg-risk-note">
                Si no apruebas el test dentro de los {toNumber(state.intentosMax, 3)} intentos configurados,
                se puede eliminar un {penaltyPctConfigured}% de tu progreso de este nivel.
              </span>
            ) : !isApproved && !isNoTestFinished && penaltyPctConfigured === 0 ? (
              <span className="tg-risk-note">
                Este test no descuenta progreso si agotas los intentos.
              </span>
            ) : null}
          </div>
        </div>
        <div className="tg-right">
          {isApproved ? (
            <>
              <span className="tg-approved">Aprobado ✓</span>
              <button className="tg-btn primary" onClick={() => navigate(`/curso/${codigoCurso}/nivel/${nivel.id}/test/review`)}>Revisar</button>
            </>
          ) : isNoTestFinished ? (
            <span className="tg-approved">Nivel finalizado ✓</span>
          ) : isLockedByPenalty ? (
            <span className="tg-locked-pill">Recompleta el nivel</span>
          ) : isAttemptsExhausted ? (
            <span className="tg-locked-pill">Sin intentos</span>
          ) : (
            <>
              <button className="tg-btn continue-test" onClick={goToTest}>
                <Play size={16} /> {isInProgress ? "Continuar intento" : "Rendir Test"}
              </button>
              <Attempts />
            </>
          )}
          {canDownloadReport && (
            <button className="tg-btn secondary" onClick={() => onDownloadLevelReport?.(nivel)} disabled={Number(downloadingLevelId) === Number(nivel.id)}>
              <FileDown size={16} /> Reporte
            </button>
          )}
        </div>
      </div>

      {penaltyNotice && hasPenalty && !isApproved ? (
        <div className="tg-penalty-note">
          Se eliminó un {toNumber(penaltyNotice.penaltyPct, 100)}% de tu progreso de este nivel
          porque no aprobaste el test dentro de los intentos configurados.
        </div>
      ) : null}

      {showInfo && (
        <div className="ltm-backdrop" role="dialog">
          <div className="ltm-card">
            <div className="ltm-head">
              <div className="ltm-ico"><Play size={18} /></div>
              <div>
                <h3>¡Nivel completado!</h3>
                <p className="ltm-sub">Debes <strong>aprobar</strong> el test de {nivel.nombre} para avanzar.</p>
              </div>
            </div>
            <label className="ltm-check">
              <input type="checkbox" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />
              No mostrar de nuevo
            </label>
            <div className="ltm-actions">
              <button className="ltm-btn ghost" onClick={closeInfo}>Entendido</button>
              <button className="ltm-btn primary" onClick={goToTest}>Ir al test ahora</button>
            </div>
          </div>
        </div>
      )}

      {showPenaltyModal && penaltyNotice ? (
        <div className="ltm-backdrop" role="dialog">
          <div className="ltm-card ltm-card-warn">
            <div className="ltm-head">
              <div className="ltm-ico ltm-ico-warn"><Info size={18} /></div>
              <div>
                <h3>Tu progreso del nivel fue ajustado</h3>
                <p className="ltm-sub">
                  Se eliminó un <strong>{toNumber(penaltyNotice.penaltyPct, 100)}%</strong> de tu progreso
                  porque no aprobaste el test dentro de los intentos configurados.
                </p>
              </div>
            </div>
            <div className="ltm-actions">
              <button className="ltm-btn primary" onClick={closePenaltyModal}>Entendido</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

const LevelSection = React.memo(function LevelSection({
  nivel,
  codigoCurso,
  isLevelCompleted,
  isLevelStructureCompleted,
  getStructureProgress,
  calculateRealProgress,
  onDownloadLevelReport,
  downloadingLevelId,
  canDownloadReport,
  onTestStatus,
  refreshKey,
  typeStyle,
  filter,
  setFilter,
  openLevels,
  setOpenLevels,
  navigate,
  filterComponents,
  getIcon,
  isCompletedItem,
  isUnlockedItem,
  handleComponentClick,
  registerRef,
  location,
  onOpenInfo,
}) {
  const t = typeStyle[nivel.tipo] || {};
  const progress = toNumber(calculateRealProgress(nivel), 0);
  const isLevelOpen = openLevels[nivel.id] !== false;
  const unlocked = isUnlockedItem(nivel);
  const completed = isLevelCompleted(nivel);

  const levelColor = completed ? '#16a34a' : t.color;
  const levelBg = completed ? '#f0fdf4' : t.bg;
  const barColor = completed ? '#16a34a' : t.color;

  const [levelDetailsOpen, toggleLevelDetails] = useStickyToggle(
    `cd:levelDetails:${codigoCurso}:${nivel.id}`,
    false,
  );

  return (
    <section
      className="level-section"
      style={
        completed
          ? { borderColor: '#bbf7d0', background: 'rgba(240,253,244,0.4)' }
          : !unlocked
            ? { borderColor: '#cbd5e1', background: 'rgba(248,250,252,0.85)', opacity: 0.78 }
            : {}
      }
    >
      <header className="level-header" data-tour="level-section">
        <div className="level-title">
          <div
            className="level-icon"
            style={{ background: levelBg, color: levelColor }}
          >
            {completed
              ? <CheckSquare className="w-4 h-4" />
              : getIcon(nivel.tipo)
            }
          </div>
          <h2 className="level-name" style={{ color: levelColor }}>
            {nivel.nombre}
          </h2>
        </div>

        <div className="level-progress-inline">
          <LinearProgress value={progress} color={barColor} height={10} />
          <span className="progress-text">{progress}%</span>
        </div>

        <div className="filters-inline" style={{ position: 'relative' }}>

          {/* Botón Filtrar con dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              className="chip"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              onClick={() => setOpenLevels((s) => ({ ...s, [`filter_${nivel.id}`]: !s[`filter_${nivel.id}`] }))}
            >
              <Filter size={14} /> Filtrar
            </button>

            {openLevels[`filter_${nivel.id}`] && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,.10)',
                padding: '6px', zIndex: 50, minWidth: '160px',
                display: 'flex', flexDirection: 'column', gap: '2px',
              }}>
                {[
                  { key: 'all', label: 'Todos' },
                  { key: 'unlocked', label: 'Desbloqueados' },
                  { key: 'completed', label: 'Completados' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => {
                      setFilter(key);
                      setOpenLevels((s) => ({ ...s, [`filter_${nivel.id}`]: false }));
                    }}
                    style={{
                      textAlign: 'left', padding: '8px 12px', borderRadius: '8px',
                      border: 'none', cursor: 'pointer', fontSize: '.8rem', fontWeight: 700,
                      background: filter === key ? '#eff6ff' : 'transparent',
                      color: filter === key ? '#2563eb' : '#374151',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Botón colapsar nivel */}
          <button
            className="ghost-btn"
            onClick={() => setOpenLevels((s) => ({ ...s, [nivel.id]: !isLevelOpen }))}
            title={isLevelOpen ? "Contraer nivel" : "Expandir nivel"}
            aria-expanded={isLevelOpen}
          >
            {isLevelOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {/* Botón info del nivel */}
          <button
            className="ghost-btn"
            onClick={() => onOpenInfo(
              nivel.nombre,
              nivel.descripcion || 'Este nivel aún no tiene descripción.',
              (nivel.children || []).filter(m => m.tipo === 'modulo')
            )}
            title="Ver información del nivel"
          >
            <Info size={16} />
          </button>

        </div>
      </header>

      {/* ✅ CORRECCIÓN: Solo cargar el Gate si el nivel está ABIERTO */}
      {unlocked && isLevelOpen && (
        <LevelTestGate
          nivel={nivel}
          codigoCurso={codigoCurso}
          onStatus={onTestStatus}
          onDownloadLevelReport={onDownloadLevelReport}
          downloadingLevelId={downloadingLevelId}
          canDownloadReport={canDownloadReport}
          levelStructureReady={isLevelStructureCompleted(nivel)}
          levelCompleted={completed}
          levelProgress={progress}
          levelStructureProgress={getStructureProgress(nivel)}
          refreshKey={refreshKey}
        />
      )}

      {nivel.descripcion && levelDetailsOpen ? (
        <p className="muted level-desc">{nivel.descripcion}</p>
      ) : null}

      <div className={`modules-list ${!isLevelOpen ? "collapsed" : ""}`}>
        {!unlocked ? (
          <div className="empty-mini">Nivel bloqueado</div>
        ) : nivel.children
          ?.filter((m) => m.tipo === "modulo")
          .map((m) => (
            <ModuleSection
              key={m.id}
              mod={m}
              codigoCurso={codigoCurso}
              calculateRealProgress={calculateRealProgress}
              filterComponents={filterComponents}
              typeStyle={typeStyle}
              navigate={navigate}
              showDetails={levelDetailsOpen}
              isCompletedItem={isCompletedItem}
              isUnlockedItem={isUnlockedItem}
              handleComponentClick={handleComponentClick}
              registerRef={registerRef}
              location={location}
            />
          ))}
      </div>
    </section>
  );
});

/* =========================================================
   Modales de confirmación del tutorial
========================================================= */
function TourConfirmModal({ open, onConfirm, onCancel }) {
  if (!open) return null;

  const node = (
    <div
      className="tour-root"
      role="dialog"
      aria-modal="true"
      aria-label="Confirmación"
    >
      <div
        className="tour-dim"
        style={{
          left: 0,
          top: 0,
          width: "100vw",
          height: "100vh",
        }}
        onClick={onCancel}
      />

      <div
        className="tour-card"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tour-head">
          <strong className="tour-title">
            ¿Estás seguro de omitir estas indicaciones?
          </strong>
        </div>

        <div className="tour-text">
          <p className="tour-p">
            El tutorial te ayuda a familiarizarte con la plataforma y aprovechar
            todas sus funcionalidades para mejorar tu experiencia de
            aprendizaje.
          </p>
        </div>

        <div className="tour-actions">
          <button className="tour-btn ghost" onClick={onCancel} type="button">
            Cancelar
          </button>
          <button
            className="tour-btn primary"
            onClick={onConfirm}
            type="button"
          >
            Sí, omitir
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

function TourInfoModal({ open, onClose }) {
  if (!open) return null;

  const node = (
    <div
      className="tour-root"
      role="dialog"
      aria-modal="true"
      aria-label="Información"
    >
      <div
        className="tour-dim"
        style={{
          left: 0,
          top: 0,
          width: "100vw",
          height: "100vh",
        }}
        onClick={onClose}
      />

      <div
        className="tour-card"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tour-head">
          <strong className="tour-title">INFORMACION DISPONIBLE</strong>
        </div>

        <div className="tour-text">
          <p className="tour-p">
            Toda esta información está disponible en el apartado de{" "}
            <strong>Ayuda</strong>.
          </p>
          <p className="tour-p">
            Podrás consultarla en cualquier momento para resolver dudas sobre el
            uso de la plataforma.
          </p>
        </div>

        <div className="tour-actions">
          <button className="tour-btn primary" onClick={onClose} type="button">
            Entendido
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

/* =================
   TUTORIAL
====================*/

function TourOverlay({
  open,
  step,
  steps,
  targetEl,
  onNext,
  onBack,
  onClose,
  tutorialKey,
  forceTargetClick = false,
  showInfo,
  setShowInfo,
}) {
  const [showConfirm, setShowConfirm] = React.useState(false);
  // ✅ 1) Bloqueo de scroll
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;

    const prevent = (e) => e.preventDefault();
    window.addEventListener("wheel", prevent, { passive: false });
    window.addEventListener("touchmove", prevent, { passive: false });

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
      window.removeEventListener("wheel", prevent);
      window.removeEventListener("touchmove", prevent);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handler = (e) => {
      // Siempre permitir clicks en el modal
      if (e.target.closest(".tour-card")) return;

      const isLastStep = steps?.[step]?.key === "first_component";

      if (isLastStep) {
        // Solo permitir click en el componente enfocado
        const isTargetClick = targetEl && targetEl.contains(e.target);
        if (!isTargetClick) {
          e.preventDefault();
          e.stopPropagation();
        }
      } else {
        // Bloquear todo en pasos anteriores
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("click", handler, true);
    document.addEventListener("mousedown", handler, true);

    return () => {
      document.removeEventListener("click", handler, true);
      document.removeEventListener("mousedown", handler, true);
    };
  }, [open, step, steps, targetEl]);

  const cardRef = React.useRef(null);
  const [cardSize, setCardSize] = React.useState({ w: 560, h: 220 });

  React.useLayoutEffect(() => {
    if (!open) return;
    if (!cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    if (r.width && r.height) setCardSize({ w: r.width, h: r.height });
  }, [step, open]);

  const rect = targetEl?.getBoundingClientRect?.();
  const hasRect =
    rect &&
    Number.isFinite(rect.top) &&
    Number.isFinite(rect.left) &&
    rect.width > 0 &&
    rect.height > 0;

  const currentStepKey = steps?.[step]?.key;
  const margin = 12;
  const isRectangularStep =
    currentStepKey === "hero_left" || currentStepKey === "level_structure";

  const hole = hasRect
    ? (() => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const adaptivePad =
        currentStepKey === "level_structure"
          ? vw < 768
            ? 8
            : 16
          : vw < 768
            ? 12
            : 24;

      const margin = vw < 768 ? 8 : 12;

      if (isRectangularStep) {
        const maxWidth = vw - margin * 2;
        const maxHeight = vh - margin * 2;

        let holeWidth = rect.width + adaptivePad * 2;
        let holeHeight = rect.height + adaptivePad * 2;

        if (holeWidth > maxWidth || holeHeight > maxHeight) {
          const scaleW = maxWidth / holeWidth;
          const scaleH = maxHeight / holeHeight;
          const scale = Math.min(scaleW, scaleH, 1);

          holeWidth = holeWidth * scale;
          holeHeight = holeHeight * scale;
        }

        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        let left = centerX - holeWidth / 2;
        let top = centerY - holeHeight / 2;

        left = clamp(left, margin, vw - holeWidth - margin);
        top = clamp(top, margin, vh - holeHeight - margin);

        const cx = left + holeWidth / 2;
        const cy = top + holeHeight / 2;

        return {
          top,
          left,
          width: holeWidth,
          height: holeHeight,
          cx,
          cy,
          isRect: true,
        };
      } else {
        const w0 = rect.width + adaptivePad * 2;
        const h0 = rect.height + adaptivePad * 2;
        const d = Math.max(w0, h0, 120);
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const left = clamp(cx - d / 2, margin, vw - d - margin);
        const top = clamp(cy - d / 2, margin, vh - d - margin);
        return { top, left, width: d, height: d, cx, cy, isRect: false };
      }
    })()
    : null;

  const cardStyle = React.useMemo(() => {
    if (!hole) return {};

    const gap = 28;
    const margin = 16;
    const safeTop = 30;
    const safeBottom = 18;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const cw = cardSize.w || 560;
    const ch = cardSize.h || 220;

    let left = hole.left + hole.width / 2 - cw / 2;
    left = clamp(left, margin, vw - cw - margin);

    const currentKey = steps?.[step]?.key;
    const isLevelStructure = currentKey === "level_structure";

    let top;

    if (isLevelStructure) {
      top = hole.top - gap - ch;
      top = clamp(top, safeTop, vh - ch - safeBottom);
    } else {
      const canBottom = hole.top + hole.height + gap + ch <= vh - safeBottom;
      const canTop = hole.top - gap - ch >= safeTop;

      if (canBottom || !canTop) top = hole.top + hole.height + gap;
      else top = hole.top - gap - ch;

      top = clamp(top, safeTop, vh - ch - safeBottom);
    }

    if (vw < 640) {
      const wouldOverflow = top < safeTop || top + ch > vh - 60;

      if (wouldOverflow) {
        const availableSpace = vh - hole.height - gap * 2 - 100;

        if (availableSpace > ch) {
          top = hole.top + hole.height + gap;
          top = Math.min(top, vh - ch - 20);
        } else {
          top = vh / 2 - ch / 2;
        }
      }
    }

    return { left, top };
  }, [hole, cardSize.w, cardSize.h, steps, step]);

  if (!open) return null;

  const s = steps?.[step] || {};
  const isFirst = step === 0;
  const isLast = step === (steps?.length || 1) - 1;

  const node = (
    <>
      <div
        className="tour-root"
        role="dialog"
        aria-modal="true"
        aria-label="Tutorial"
      >
        {/* ✅ Overlay oscuro (con hueco transparente mediante box-shadow) */}
        {hole ? (
          <div
            className="tour-hole"
            style={{
              left: hole.left,
              top: hole.top,
              width: hole.width,
              height: hole.height,
              borderRadius: hole.isRect ? "18px" : "999px",
              pointerEvents: "none", // El hole es solo visual
            }}
            aria-hidden="true"
          />
        ) : (
          <div
            className="tour-dim"
            style={{
              left: 0,
              top: 0,
              width: "100vw",
              height: "100vh",
            }}
          />
        )}

        {/* ✅ Modal del tutorial */}
        <div
          ref={cardRef}
          className={`tour-card ${hole ? "has-target" : ""}`}
          style={cardStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="tour-head">
            <strong className="tour-title">{s.title || "Tutorial"}</strong>
            <span className="tour-step">
              {step + 1}/{steps.length}
            </span>
          </div>

          {s.text ? (
            <div className="tour-text">
              {String(s.text)
                .split(/\n\s*\n/g)
                .filter(Boolean)
                .map((p, i) => (
                  <p key={i} className="tour-p">
                    {p.trim()}
                  </p>
                ))}
            </div>
          ) : null}

          <div className="tour-actions">
            {/* Botón OMITIR solo en el primer paso */}
            {isFirst && (
              <button
                className="tour-btn ghost"
                onClick={() => setShowConfirm(true)}
                type="button"
              >
                Omitir
              </button>
            )}

            {!isFirst && (
              <button className="tour-btn ghost" onClick={onBack} type="button">
                Volver
              </button>
            )}

            {!isLast && (
              <button
                className="tour-btn primary"
                onClick={onNext}
                type="button"
              >
                Siguiente
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ✅ Modales de confirmación e información */}
      <TourConfirmModal
        open={showConfirm}
        onConfirm={() => {
          // Guardar que omitió el tutorial
          try {
            localStorage.setItem(tutorialKey, "1");
          } catch { }

          // Cerrar confirmación y tour
          setShowConfirm(false);
          if (typeof onClose === "function") {
            onClose();
          }

          // Mostrar modal informativo
          setShowInfo(true); // ✅ Sin setTimeout, aparece inmediatamente
        }}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  );

  return ReactDOM.createPortal(node, document.body);
}

const CursoDetalle = () => {
  const { codigoCurso } = useParams();

  // ✅ MEMORIZAR este callback es VITAL para que los niveles no causen bucles
  const handleTestStatusChange = useCallback((id, st) => {
    setLevelTestStatusMap((prev) => {
      if (JSON.stringify(prev[id]) === JSON.stringify(st)) return prev;
      return { ...prev, [id]: st };
    });
  }, []);

  const [heroOpen, toggleHeroOpen] = useStickyToggle(
    `cd:heroOpen:${codigoCurso}`,
    false,
  );
  const [continueMeta, setContinueMeta] = useState({
    show: false,
    dir: "down",
  });
  const continueRaf = useRef(null);
  const location = useLocation();

  const navigate = useNavigate();
  const { setFooterVisible } = useOutletContext() || {};

  const prevFlagsRef = useRef(null);

  const {
    fetchCourses,
    fetchStudentCourses,
    getCourseStructureStudent,
    getCourseStructureAdminPreview,
    progressLoading,
    isElementCompleted,
    isElementUnlocked,
    getElementProgress,
    getElementProgressEntry,
    initializeStudentData,
    getCourseProgress,
    getStudentStreak,
    downloadLevelReportPdf,
    getCourse,
    studentStreak,
    utmCoins,

  } = useFuncion();

  const fetchCoursesRef = useRef(fetchCourses);
  const fetchStudentCoursesRef = useRef(fetchStudentCourses);
  const getCourseStructureStudentRef = useRef(getCourseStructureStudent);
  const getCourseStructureAdminPreviewRef = useRef(getCourseStructureAdminPreview);
  const initializeStudentDataRef = useRef(initializeStudentData);
  const getStudentStreakRef = useRef(getStudentStreak);
  const downloadLevelReportPdfRef = useRef(downloadLevelReportPdf);
  const getCourseRef = useRef(getCourse);

  useEffect(() => {
    fetchCoursesRef.current = fetchCourses;
    fetchStudentCoursesRef.current = fetchStudentCourses;
    getCourseStructureStudentRef.current = getCourseStructureStudent;
    getCourseStructureAdminPreviewRef.current = getCourseStructureAdminPreview;
    initializeStudentDataRef.current = initializeStudentData;
    getStudentStreakRef.current = getStudentStreak;
    downloadLevelReportPdfRef.current = downloadLevelReportPdf;
    getCourseRef.current = getCourse;
  }, [
    fetchCourses,
    fetchStudentCourses,
    getCourseStructureStudent,
    getCourseStructureAdminPreview,
    initializeStudentData,
    getStudentStreak,
    downloadLevelReportPdf,
    getCourse,
  ]);


  const { user } = useAuth();

  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Rol activo del usuario para controlar visibilidad del panel
  const [activeRoleStored, setActiveRoleStored] = useState(
    () => localStorage.getItem('activeRole') || localStorage.getItem('currentRole') || ''
  );

  useEffect(() => {
    const sync = () => {
      setActiveRoleStored(
        localStorage.getItem('activeRole') || localStorage.getItem('currentRole') || ''
      );
    };
    window.addEventListener('storage', sync);
    window.addEventListener('roleChanged', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('roleChanged', sync);
    };
  }, []);

  const userRole = useMemo(() => {
    // 1. localStorage: fuente más confiable (se actualiza al cambiar rol)
    const storedRole = activeRoleStored.toLowerCase();
    if (storedRole) return storedRole;

    // 2. Campo directo en user
    const direct = String(
      user?.rol_activo ?? user?.currentRole ?? user?.role ?? user?.rol ?? ''
    ).toLowerCase();
    if (direct) return direct;

    // 3. course_roles: rol en este curso específico
    if (user?.course_roles?.docente === true) return 'docente';
    if (user?.course_roles?.asistente === true) return 'asistente';

    // 4. Roles globales
    const rolesArr = Array.isArray(user?.roles)
      ? user.roles.map(r => String(r).toLowerCase()) : [];
    if (rolesArr.includes('administrador')) return 'administrador';
    if (rolesArr.includes('admin_facultad')) return 'admin_facultad';
    if (user?.course_roles) return 'estudiante';
    if (rolesArr.includes('docente')) return 'docente';
    if (rolesArr.includes('asistente')) return 'asistente';

    return 'estudiante';
  }, [user, activeRoleStored]);

  const isManagement = useMemo(() =>
    ['administrador', 'docente', 'admin_facultad', 'asistente'].includes(userRole),
    [userRole]);

  const [curso, setCurso] = useState(null);
  const [estructura, setEstructura] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstComponentId, setFirstComponentId] = useState(null);
  const [managementExpanded, setManagementExpanded] = useState(true);
  const [courseSimulation, setCourseSimulation] = useState('start');
  // Overrides visuales según simulación — no toca el backend
  const simOverride = useMemo(() => {
    if (!isManagement || courseSimulation === 'start') return null;
    return courseSimulation; // 'mid' | 'full'
  }, [isManagement, courseSimulation]);

  const [openLevels, setOpenLevels] = useState({});
  const [filter, setFilter] = useState("all");

  const [downloadingLevelId, setDownloadingLevelId] = useState(null);

  // Estado del test de nivel por nivel (para habilitar el reporte solo si ya completó el test)
  const [levelTestStatusMap, setLevelTestStatusMap] = useState({});

  const handleDownloadLevelReport = useCallback(async (nivel) => {
    if (!nivel?.id) return;
    try {
      setDownloadingLevelId(Number(nivel.id));
      const { blob, filename } = await downloadLevelReportPdfRef.current?.(nivel.id);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `reporte_nivel_${nivel.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error descargando reporte de nivel:", e);
      alert(e?.message || "No se pudo descargar el reporte del nivel.");
    } finally {
      setDownloadingLevelId(null);
    }
  }, []);

  const [infoModal, setInfoModal] = useState({ open: false, title: '', content: '' });
  const [openStreak, setOpenStreak] = useState(false);

  /* ==== HERO sticky sentinel ==== */
  const [stuck, setStuck] = useState(false);

  /* ==== Refs de componentes para “Continuar” ==== */
  const compRefs = useRef(new Map());
  const componentContextRef = useRef(new Map());
  const registerRef = useCallback((id, el) => {
    const m = compRefs.current;
    if (el) m.set(id, el);
    else m.delete(id);
  }, []);

  // ===== Tutorial (solo 1 vez por estudiante) =====
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourLockFirstClick, setTourLockFirstClick] = useState(false);
  const [showTourInfo, setShowTourInfo] = useState(false);
  const [progressRefreshTick, setProgressRefreshTick] = useState(0);

  // Identificador del estudiante para la cache (ajústalo a tu fuente real)
  const userKey = useMemo(
    () =>
      user?.local?.id ||
      user?.estudiante_id ||
      user?.student_id ||
      user?.id ||
      user?.email ||
      getUserKeyFromStorage(),
    [user?.local?.id, user?.estudiante_id, user?.student_id, user?.id, user?.email],
  );
  const userScope = useMemo(() => safeKey(userKey || "anon"), [userKey]);
  const tutorialKey = useMemo(
    () => getTutorialCacheKey(codigoCurso, userKey),
    [codigoCurso, userKey],
  );

  useEffect(() => {
    const onStudentProgressUpdated = (event) => {
      const detail = event?.detail || {};
      if (detail?.userScope && detail.userScope !== userScope) return;
      if (
        detail?.codigoCurso &&
        String(detail.codigoCurso) !== String(codigoCurso)
      ) {
        return;
      }
      setProgressRefreshTick((value) => value + 1);
    };

    window.addEventListener(
      "student-course-progress-updated",
      onStudentProgressUpdated,
    );
    return () =>
      window.removeEventListener(
        "student-course-progress-updated",
        onStudentProgressUpdated,
      );
  }, [codigoCurso, userScope]);

  const TOUR_STEPS = useMemo(
    () => [
      {
        key: "welcome",
        title: "BIENVENIDO AL CURSO",
        text:
          "Esta plataforma está diseñada para fortalecer tu aprendizaje de forma progresiva, guiándote a través de rutas estructuradas y evaluaciones.\n\n" +
          "A continuación, realizarás un recorrido breve para identificar los elementos principales de navegación y seguimiento académico.",
        target: null,
      },
      {
        key: "hero_left",
        title: "ENCABEZADO DEL CURSO Y PROGRESO GLOBAL",
        text:
          "Aquí se muestra el nombre Y código del curso, además de poder visualizar una descripción general mediante el ícono del ojo.\n\n" +
          "También verás el progreso global del curso, que se actualiza conforme completes actividades, tareas o evaluaciones.",
        target: () => document.querySelector("[data-tour='hero-left']"),
      },
      {
        key: "streak",
        title: "RACHA DE APRENDIZAJE",
        text:
          "La racha reconoce tu constancia. Al completar una tarea o un test, se registra tu avance del día y podrás reclamar recompensas.\n\n" +
          "Mantener continuidad favorece tu avance y promueve hábitos de estudio sostenidos.",
        target: () => document.querySelector("[data-tour='streak-card']"),
      },
      {
        key: "coins",
        title: "UTMCOINS",
        text:
          "Las UTMCoins son incentivos acumulables. Podrás obtenerlas al avanzar en el curso completando tareas y test.\n\n" +
          "Más adelante podrás usarlas para comprar comodines que te ayuden durante las actividades.",
        target: () => document.querySelector("[data-tour='coins-card']"),
      },
      {
        key: "level_structure",
        title: "ESTRUCTURA DEL CURSO",
        text:
          "El curso se organiza por niveles. Cada nivel integra módulos que contienen componentes (unidades de trabajo).\n\n" +
          "Desde el botón 'Recursos' podrás acceder a materiales de apoyo (PDFs, documentos y otros archivos) que el docente comparta por módulo.\n\n" +
          "Cuando completes todos los componentes de cada módulo se habilitará la opción de dar el Test del nivel para poner a prueba  " +
          "tus conocimientos.",
        target: () => document.querySelector("[data-tour='first-module']"),
      },
      {
        key: "first_component",
        title: "INICIA TU APRENDIZAJE",
        text: "Para comenzar, selecciona el primer componente disponible marcado como 'Siguiente'.\n\n",
        target: () => compRefs.current.get(firstComponentId),
      },
    ],
    [firstComponentId],
  );

  /* =========================================================
     Init data (sin cambios de UI)
  ========================================================= */
  useEffect(() => {
    initializeStudentData?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigoCurso]); // 👈 Quitamos initializeStudentData de aquí

  useEffect(() => {
    getStudentStreak?.().catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigoCurso]); // 👈 Quitamos getStudentStreak de aquí

  /* =========================================================
     Flags en sessionStorage (igual que antes, optimizando stringify)
  ========================================================= */
  useEffect(() => {
    if (!curso || progressLoading) return;

    const rawMin =
      curso.porcentaje_minimo_curso ??
      curso.porcentaje_minimo_aprobacion ??
      60;
    const min = Math.max(0, Math.min(100, Number(rawMin) || 100));
    const progress = Number(getCourseProgress?.(curso.id)) || 0;
    const isExamOnly =
      String(curso?.tipo || "").toLowerCase() === "examen_solo";
    const enrollmentStatus = curso?.estado;
    const finalGrade =
      curso?.nota_final != null ? Number(curso.nota_final) : null;

    const eligible =
      isExamOnly ||
      enrollmentStatus === "completado" ||
      (finalGrade != null ? finalGrade >= min : progress >= min);

    const flags = {
      examEligible: !!eligible,
      completedCourse: !!eligible,
      minRequired: min,
      progress,
    };

    const prev = prevFlagsRef.current;
    const changed =
      !prev ||
      prev.examEligible !== flags.examEligible ||
      prev.completedCourse !== flags.completedCourse ||
      prev.minRequired !== flags.minRequired ||
      prev.progress !== flags.progress;

    if (changed) {
      prevFlagsRef.current = flags;

      try {
        sessionStorage.setItem(
          `courseFlags:${curso.codigo_curso}`,
          JSON.stringify(flags),
        );
      } catch { }

      try {
        window.dispatchEvent(
          new CustomEvent("courseFlagsUpdated", {
            detail: {
              codigoCurso: curso.codigo_curso,
              flags,
            },
          })
        );
      } catch { }
    }
  }, [curso, progressLoading, getCourseProgress]);

  /* =========================================================
     Loader optimizado (course cache + structure cache + dedupe + hash)
  ========================================================= */
  const loadRunRef = useRef(0);

  useEffect(() => {
    if (!codigoCurso) return;

    let alive = true;
    const runId = ++loadRunRef.current;

    setLoading(true);

    // 🔒 Helpers (TU lógica - no se pierde)
    const loadCourseFromBackend = async () => {
      const key = codigoCurso;
      if (inFlightCourse.has(key)) return inFlightCourse.get(key);

      const p = (async () => {
        const currentUser = userRef.current;

        const roles = Array.isArray(currentUser?.roles)
          ? currentUser.roles.map((r) =>
            String(r?.nombre ?? r?.rol ?? r?.role ?? r).toLowerCase()
          )
          : [];

        const roleActivo = String(
          currentUser?.rol_activo ??
          currentUser?.currentRole ??
          currentUser?.role ??
          currentUser?.rol ??
          ""
        ).toLowerCase();

        const allRoles = new Set([...roles, roleActivo].filter(Boolean));
        // Admin/docente: obtener ID desde query param ?preview_id=
        const previewId = new URLSearchParams(window.location.search).get("preview_id");

        if (previewId && !isNaN(Number(previewId))) {
          try {
            const direct = await getCourseRef.current?.(Number(previewId));
            if (direct) return direct;
          } catch { }
        }

        const isStudent = allRoles.has("estudiante");
        const isAdmin = allRoles.has("administrador") || allRoles.has("admin") || allRoles.has("superadmin");

        if (isStudent) {
          const studentData = await fetchStudentCoursesRef.current?.({
            page: 1,
            limit: 200,
          });
          const list = Array.isArray(studentData)
            ? studentData
            : studentData?.courses || [];
          return list.find((c) => c.codigo_curso === codigoCurso) || null;
        }

        // Si es admin, usar fetchStudentCourses no aplica — buscar como estudiante simulado
        if (isAdmin) {
          try {
            const studentData = await fetchStudentCoursesRef.current?.({
              page: 1,
              limit: 200,
            });
            const list = Array.isArray(studentData)
              ? studentData
              : studentData?.items || studentData?.courses || [];
            const found = list.find((c) => c.codigo_curso === codigoCurso);
            if (found) return found;
          } catch { }
        }

        // Fallback: buscar en listado completo
        const allCourses = await fetchCoursesRef.current?.({ limit: 500, showAll: true });
        const allList = Array.isArray(allCourses)
          ? allCourses
          : allCourses?.courses || [];
        return allList.find((c) => c.codigo_curso === codigoCurso) || null;
      })();

      inFlightCourse.set(key, p);
      try {
        return await p;
      } finally {
        inFlightCourse.delete(key);
      }
    };

    const loadStructureFromBackend = async (courseId) => {
      if (inFlightStructure.has(courseId)) return inFlightStructure.get(courseId);

      const p = (async () => {
        const previewId = new URLSearchParams(window.location.search).get("preview_id");
        const useAdminPreview =
          previewId &&
          !isNaN(Number(previewId)) &&
          Number(previewId) === Number(courseId);

        const structureData = useAdminPreview
          ? await getCourseStructureAdminPreviewRef.current?.(courseId)
          : await getCourseStructureStudentRef.current?.(courseId);
        return structureData?.structure || [];
      })();

      inFlightStructure.set(courseId, p);
      try {
        return await p;
      } finally {
        inFlightStructure.delete(courseId);
      }
    };

    // 🚀 FUNCIÓN PRINCIPAL
    const loadAllData = async () => {
      try {
        const cacheKeyMeta = cacheManager.getCoursMetaCacheKey(codigoCurso, userScope);

        const cached = cacheManager.readCache(cacheKeyMeta, {
          storageType: "session",
          currentVersion: cacheManager.CURRENT_VERSIONS.COURSE_META,
        });

        let currentCourse = cached?.course || null;

        // 🔹 Mostrar cache inmediato
        if (currentCourse && alive && loadRunRef.current === runId) {
          setCurso(currentCourse);
        }

        // 🔹 Revalidar si no es fresh
        if (!cached?.isFresh) {
          try {
            const fresh = await loadCourseFromBackend();
            if (!alive || loadRunRef.current !== runId) return;

            if (fresh) {
              currentCourse = fresh;
              setCurso(fresh);

              cacheManager.writeCache(cacheKeyMeta, { course: fresh }, {
                storageType: "session",
                ttl: cacheManager.DEFAULT_TTL.COURSE_META,
                cacheVersion: cacheManager.CURRENT_VERSIONS.COURSE_META,
              });
            }
          } catch (e) {
            console.warn("Fallback a cache de curso", e);
          }
        }

        if (!alive || !currentCourse) {
          setCurso(null);
          setEstructura([]);
          setFirstComponentId(null);
          return;
        }

        if (String(currentCourse.tipo) === "examen_solo") {
          setEstructura([]);
          setFirstComponentId(null);
          return;
        }

        // 🔹 CACHE DE ESTRUCTURA
        const cacheKeyStruct = cacheManager.getStructureCacheKey(currentCourse.id, userScope);

        const cachedStruct = cacheManager.readCache(cacheKeyStruct, {
          storageType: "local",
          currentVersion: cacheManager.CURRENT_VERSIONS.COURSE_STRUCTURE,
          validateVersion: true,
        });

        // 🔹 BACKEND STRUCT + HASH CHECK
        try {
          const struct = await loadStructureFromBackend(currentCourse.id);
          if (!alive || loadRunRef.current !== runId) return;

          const newHash = JSON.stringify(struct);
          const oldHash = cachedStruct?.hash;
          const firstId = getFirstComponentIdFromStructure(struct);

          setEstructura(struct);
          setFirstComponentId(firstId);

          if (oldHash !== newHash) {
            cacheManager.writeCache(
              cacheKeyStruct,
              {
                structure: struct,
                firstComponentId: firstId,
                hash: newHash,
              },
              {
                storageType: "local",
                ttl: cacheManager.DEFAULT_TTL.COURSE_STRUCTURE,
                cacheVersion: cacheManager.CURRENT_VERSIONS.COURSE_STRUCTURE,
                includeStructureVersion: true,
              }
            );
          }
        } catch (err) {
          console.warn("Fallback a cache estructura", err);
        }

      } catch (error) {
        console.error("Error cargando el curso:", error);
      } finally {
        if (alive && loadRunRef.current === runId) {
          setLoading(false);
        }
      }
    };

    loadAllData();

    return () => { alive = false; };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigoCurso, userScope, progressRefreshTick]);

  useEffect(() => {
    cacheManager.clearExpiredCache();
  }, []);

  /* =========================================================
     Footer visibility (igual, pero evitando recrear handlers)
  ========================================================= */
  useEffect(() => {
    if (setFooterVisible && (loading || progressLoading))
      setFooterVisible(false);
  }, [loading, progressLoading, setFooterVisible]);

  const assessFooterRef = useRef(null);

  useEffect(() => {
    if (!setFooterVisible || loading || progressLoading) return;

    const container = document.querySelector(".course-detail") || document.body;

    const assess = () => {
      setFooterVisible(
        document.documentElement.scrollHeight > window.innerHeight + 1,
      );
    };

    // throttle simple (evita spam)
    let raf = null;
    const assessThrottled = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        assess();
      });
    };

    assessFooterRef.current = assessThrottled;

    assess();
    window.addEventListener("resize", assessThrottled);

    let ro, mo;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(assessThrottled);
      ro.observe(container);
    }

    const imgs = Array.from(document.images);
    const onImgLoad = () => assessThrottled();
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", onImgLoad);
    });

    if ("MutationObserver" in window) {
      mo = new MutationObserver(assessThrottled);
      mo.observe(container, { childList: true, subtree: true });
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", assessThrottled);
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      imgs.forEach((img) => img.removeEventListener("load", onImgLoad));
    };
  }, [loading, progressLoading, estructura, curso, filter, setFooterVisible]);

  useEffect(() => () => setFooterVisible?.(true), [setFooterVisible]);

  /* =========================================================
     Helpers estables (importantísimo para React.memo)
  ========================================================= */
  // Helper: lista plana de todos los componentes en orden del curso
  const allComponentsOrdered = useMemo(() => {
    const out = [];
    for (const niv of estructura || []) {
      for (const mod of niv?.children || []) {
        if (mod.tipo !== 'modulo') continue;
        for (const comp of mod?.children || []) {
          if (comp.tipo === 'componente') out.push(comp.id);
        }
      }
    }
    return out;
  }, [estructura]);

  // Set de IDs completados según simulación
  const simCompletedIds = useMemo(() => {
    if (!simOverride) return null;
    if (simOverride === 'full') return new Set(allComponentsOrdered);
    if (simOverride === 'mid') {
      const mitad = Math.ceil(allComponentsOrdered.length / 2);
      return new Set(allComponentsOrdered.slice(0, mitad));
    }
    return null;
  }, [simOverride, allComponentsOrdered]);

  const calculateRealProgress = useCallback(
    (item) => {
      if (simCompletedIds) {
        if (item.tipo === 'componente') {
          return simCompletedIds.has(item.id) ? 100 : 0;
        }
        if (item.tipo === 'modulo') {
          const comps = (item.children || []).filter(c => c.tipo === 'componente');
          if (!comps.length) return 0;
          const done = comps.filter(c => simCompletedIds.has(c.id)).length;
          return Math.round((done / comps.length) * 100);
        }
        if (item.tipo === 'nivel') {
          const mods = (item.children || []).filter(m => m.tipo === 'modulo');
          if (!mods.length) return 0;
          let sum = 0;
          for (const mod of mods) {
            const comps = (mod.children || []).filter(c => c.tipo === 'componente');
            if (!comps.length) continue;
            const done = comps.filter(c => simCompletedIds.has(c.id)).length;
            sum += (done / comps.length) * 100;
          }
          return Math.round(sum / mods.length);
        }
      }
      return curso ? toNumber(getElementProgress(curso.id, item.id), 0) : 0;
    },
    [curso, getElementProgress, simCompletedIds],
  );

  const isCompletedItem = useCallback(
    (item) => {
      if (simCompletedIds) {
        if (item.tipo === 'componente') return simCompletedIds.has(item.id);
        if (item.tipo === 'modulo') {
          const comps = (item.children || []).filter(c => c.tipo === 'componente');
          return comps.length > 0 && comps.every(c => simCompletedIds.has(c.id));
        }
        if (item.tipo === 'nivel') {
          const mods = (item.children || []).filter(m => m.tipo === 'modulo');
          return mods.length > 0 && mods.every(mod => {
            const comps = (mod.children || []).filter(c => c.tipo === 'componente');
            return comps.length > 0 && comps.every(c => simCompletedIds.has(c.id));
          });
        }
      }
      return curso ? isElementCompleted(curso.id, item.id) : false;
    },
    [curso, isElementCompleted, simCompletedIds],
  );

  const isLevelStructureCompleted = useCallback(
    (nivel) => {
      if (!nivel) return false;
      if (isCompletedItem(nivel)) return true;
      if (curso) {
        const entry = getElementProgressEntry?.(curso.id, nivel.id);
        const total = Number(entry?.total_elementos ?? 0);
        const completedCount = Number(entry?.elementos_completados ?? 0);
        if (total > 0 && completedCount >= total) return true;
        if (
          Number(entry?.porcentaje_completado ?? 0) >= 100 &&
          !asBool(entry?.completado, false)
        ) {
          return true;
        }
      }
      return isStructuralBranchCompleted(
        nivel,
        isCompletedItem,
        calculateRealProgress,
      );
    },
    [calculateRealProgress, curso, getElementProgressEntry, isCompletedItem],
  );

  const getStructureProgress = useCallback(
    (nivel) => {
      if (!curso || !nivel) return 0;
      const entry = getElementProgressEntry?.(curso.id, nivel.id);
      const total = Number(entry?.total_elementos ?? 0);
      const completedCount = Number(entry?.elementos_completados ?? 0);

      if (total > 0) {
        return Math.round((completedCount / total) * 100);
      }

      return Number(entry?.porcentaje_completado ?? 0);
    },
    [curso, getElementProgressEntry],
  );

  // Inicializar y actualizar colapso de niveles cuando estructura Y progreso estén listos
  useEffect(() => {
    if (!estructura.length || !curso || progressLoading) return;

    setOpenLevels((prev) => {
      const initial = {};
      let foundIncomplete = false;

      estructura.forEach((nivel) => {
        const pct = toNumber(calculateRealProgress(nivel), 0);
        const done = isElementCompleted(curso.id, nivel.id) || pct >= 100;

        if (done) {
          // Nivel completado: siempre cerrado
          initial[nivel.id] = false;
        } else if (!foundIncomplete) {
          // Primer nivel incompleto: abierto
          initial[nivel.id] = true;
          foundIncomplete = true;
        } else {
          // Niveles incompletos posteriores: cerrados
          initial[nivel.id] = false;
        }

        // Preservar estado del filtro si ya existía
        const filterKey = `filter_${nivel.id}`;
        if (filterKey in prev) {
          initial[filterKey] = prev[filterKey];
        }
      });

      return initial;
    });
  }, [estructura, curso, progressLoading, calculateRealProgress, isElementCompleted]);

  const isUnlockedItem = useCallback(
    (item) => {
      if (simCompletedIds) return true;
      if (!curso) return false;
      const baseUnlocked = isElementUnlocked(curso.id, item.id);
      if (item.tipo === 'componente') {
        const ctx = componentContextRef.current.get(item.id);
        const modulo = ctx?.modulo;
        if (modulo) {
          const rawSeq = modulo.secuencia_hijos ?? modulo.secuencial_hijos;
          const seqModulo = flagToBoolean(rawSeq, true);
          if (!seqModulo) {
            const siblings = ctx.siblings || [];
            const moduloUnlocked = isElementUnlocked(curso.id, modulo.id);
            const anyUnlockedInModule = siblings.some((comp) => {
              return isElementUnlocked(curso.id, comp.id);
            });
            if (moduloUnlocked || anyUnlockedInModule) return true;
            return baseUnlocked;
          }
          return baseUnlocked;
        }
      }
      return baseUnlocked;
    },
    [curso, isElementUnlocked, simCompletedIds],
  );

  const isLevelCompleted = useCallback(
    (nivel) =>
      curso
        ? isElementCompleted(curso.id, nivel.id) ||
        calculateRealProgress(nivel) >= 100
        : false,
    [curso, isElementCompleted, calculateRealProgress],
  );

  const getIcon = useCallback((type) => {
    return (
      {
        nivel: <BookOpen className="w-4 h-4" />,
        modulo: <Layers className="w-4 h-4" />,
        componente: <Package className="w-4 h-4" />,
        tarea: <CheckSquare className="w-4 h-4" />,
        actividad: <Play className="w-4 h-4" />,
      }[type] || <BookOpen className="w-4 h-4" />
    );
  }, []);

  const typeStyle = useMemo(
    () => ({
      nivel: {
        color: "#1e6f4a",
        bg: "linear-gradient(135deg,#e8f5f1,#f5fffb)",
      },
      modulo: {
        color: "#0f5fb8",
        bg: "linear-gradient(135deg,#e8f1fb,#f5f9ff)",
      },
      componente: {
        color: "#6b1fa2",
        bg: "linear-gradient(135deg,#f4e9fb,#fcf7ff)",
      },
      tarea: {
        color: "#d96b00",
        bg: "linear-gradient(135deg,#fff1e6,#fff8f2)",
      },
      actividad: {
        color: "#b31919",
        bg: "linear-gradient(135deg,#fdeaea,#fff7f7)",
      },
    }),
    [],
  );

  const handleComponentClick = useCallback(
    (component) => {
      if (!curso) return;

      // Si el tutorial está abierto y estamos en el paso final (paso #6):
      const isFinalStep =
        tourOpen && TOUR_STEPS?.[tourStep]?.key === "first_component";
      if (isFinalStep) {
        // Solo permite clic en el primer componente
        if (component.id !== firstComponentId) {
          setTourLockFirstClick(true);
          return;
        }

        // Marcar tutorial como completado
        try {
          localStorage.setItem(tutorialKey, "1");
        } catch { }

        setTourOpen(false);
        setTourLockFirstClick(false);

        // continúa a la tarea
        navigate(`/curso/${codigoCurso}/tarea/${component.id}`);
        return;
      }

      // flujo normal
      if (isUnlockedItem(component))
        navigate(`/curso/${codigoCurso}/tarea/${component.id}`);
    },
    [
      curso,
      tourOpen,
      tourStep,
      TOUR_STEPS,
      firstComponentId,
      tutorialKey,
      navigate,
      codigoCurso,
      isUnlockedItem,
    ],
  );

  const filterComponents = useCallback(
    (items = []) => {
      switch (filter) {
        case "completed":
          return items.filter(isCompletedItem);
        case "unlocked":
          return items.filter(isUnlockedItem);
        default:
          return items;
      }
    },
    [filter, isCompletedItem, isUnlockedItem],
  );

  /* =========================================================
     Ring animation (sin cambiar UI, más estable con ref)
  ========================================================= */
  const courseProgress = useMemo(() => {
    if (simCompletedIds) {
      const total = allComponentsOrdered.length;
      if (!total) return 0;
      return Math.round((simCompletedIds.size / total) * 100);
    }
    return curso ? toNumber(getCourseProgress(curso.id), 0) : 0;
  }, [simCompletedIds, allComponentsOrdered, curso, getCourseProgress]);

  const [ringValue, setRingValue] = useState(0);
  const ringRef = useRef(0);

  useEffect(() => {
    const t0 = performance.now();
    const start = ringRef.current;
    const end = toNumber(courseProgress, 0);
    let raf;

    const step = (now) => {
      const p = Math.min(1, (now - t0) / 800);
      const v = Math.round(start + (end - start) * easeOutCubic(p));
      ringRef.current = v;
      setRingValue(v);
      if (p < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [courseProgress]);

  /* =========================================================
     "Continuar" optimizado: flatten + rAF throttle
  ========================================================= */
  const orderedComponents = useMemo(() => {
    const out = [];
    const ctx = new Map();

    for (const niv of estructura || []) {
      if (!niv || !Array.isArray(niv.children)) continue;

      for (const mod of niv.children) {
        if (!mod || mod.tipo !== "modulo" || !Array.isArray(mod.children))
          continue;

        const comps = mod.children.filter((c) => c && c.tipo === "componente");
        comps.forEach((c, index) => {
          out.push(c);
          ctx.set(c.id, {
            nivel: niv,
            modulo: mod,
            index,
            siblings: comps,
          });
        });
      }
    }

    // ✅ Guardamos el mapa para que lo use isUnlockedItem
    componentContextRef.current = ctx;

    return out;
  }, [estructura]);

  const nextTarget = useMemo(() => {
    for (const c of orderedComponents) {
      if (isUnlockedItem(c) && !isCompletedItem(c)) return c;
    }
    return null;
  }, [orderedComponents, isUnlockedItem, isCompletedItem]);

  // Auto-scroll SOLO al entrar al curso (una vez por montaje)
  const autoScrolledRef = useRef(false);

  // Ref que impide que el tour se abra más de una vez por montaje del componente
  const tourOpenedRef = useRef(false);

  useEffect(() => {
    // Resetear al cambiar de curso
    tourOpenedRef.current = false;
  }, [codigoCurso]);

  useEffect(() => {
    if (loading || progressLoading) return;
    if (!curso) return;
    if (!firstComponentId) return;

    // Esperar a que userKey sea real (no anónimo)
    if (!userKey || userKey === "anon") return;

    // Ya se abrió en este montaje, no volver a abrir
    if (tourOpenedRef.current) return;

    try {
      const done = localStorage.getItem(tutorialKey) === "1";
      if (done) return;
    } catch { }

    // Marcar que ya se evaluó en este montaje ANTES de abrir
    tourOpenedRef.current = true;

    setTourStep(0);
    setTourOpen(true);
    setTourLockFirstClick(false);
  }, [loading, progressLoading, curso, firstComponentId, tutorialKey, userKey]);

  useEffect(() => {
    if (loading || progressLoading) return;
    if (!nextTarget) return;

    // solo 1 vez por entrada (montaje de esta pantalla)
    if (autoScrolledRef.current) return;
    autoScrolledRef.current = true;

    let cancelled = false;

    const margin = 120;

    const tryScroll = (tries = 0) => {
      if (cancelled) return;

      const el = compRefs.current.get(nextTarget.id);
      if (el) {
        const r = el.getBoundingClientRect();
        const isVisible =
          r.bottom > margin && r.top < window.innerHeight - margin;

        if (!isVisible) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("scroll-highlight");
          setTimeout(() => el.classList.remove("scroll-highlight"), 1600);
        }
        return;
      }

      // espera a que se registren refs
      if (tries < 28) requestAnimationFrame(() => tryScroll(tries + 1));
    };

    requestAnimationFrame(() => setTimeout(() => tryScroll(0), 60));

    return () => {
      cancelled = true;
    };
  }, [loading, progressLoading, nextTarget]);

  const updateContinueVisibility = useCallback(() => {
    if (continueRaf.current) return;

    continueRaf.current = requestAnimationFrame(() => {
      continueRaf.current = null;

      const c = nextTarget;
      if (!c) {
        setContinueMeta((p) => (p.show ? { ...p, show: false } : p));
        return;
      }

      const el = compRefs.current.get(c.id);
      if (!el) {
        // si aún no hay ref, mostramos para que el usuario pueda ir
        setContinueMeta((p) =>
          p.show && p.dir === "down" ? p : { show: true, dir: "down" },
        );
        return;
      }

      const r = el.getBoundingClientRect();
      const margin = 120;

      // Visible si está dentro del viewport con margen (para evitar “parpadeo”)
      const isVisible =
        r.bottom > margin && r.top < window.innerHeight - margin;

      if (isVisible) {
        setContinueMeta((p) => (p.show ? { ...p, show: false } : p));
        return;
      }

      // Si está arriba, dir="up"; si está abajo, dir="down"
      const dir = r.top < margin ? "up" : "down";
      setContinueMeta((p) =>
        p.show && p.dir === dir ? p : { show: true, dir },
      );
    });
  }, [nextTarget]);

  useEffect(() => {
    updateContinueVisibility();
  }, [nextTarget, updateContinueVisibility]);

  useEffect(() => {
    const onScroll = () => updateContinueVisibility();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (continueRaf.current) cancelAnimationFrame(continueRaf.current);
      continueRaf.current = null;
    };
  }, [updateContinueVisibility]);

  const scrollToNextTarget = useCallback(() => {
    const c = nextTarget;
    if (!c) return;
    const el = compRefs.current.get(c.id);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("scroll-highlight");
      setTimeout(() => el.classList.remove("scroll-highlight"), 1600);
    } else {
      handleComponentClick(c);
    }
  }, [nextTarget, handleComponentClick]);

  /* =========================================================
     HERO sticky sentinel (igual)
  ========================================================= */
  useEffect(() => {
    const sentinel = document.getElementById(`hero-sentinel-${codigoCurso}`);
    if (!sentinel) return;
    const obs = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), {
      threshold: 1,
    });
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [codigoCurso]);


  const handleLevelStatus = useCallback((nivelId, st) => {
    const key = Number(nivelId);
    if (!key) return;

    setLevelTestStatusMap((prev) => {
      const current = prev[key];

      const prevRaw = current ? JSON.stringify(current) : "";
      const nextRaw = st ? JSON.stringify(st) : "";

      if (prevRaw === nextRaw) return prev;

      return {
        ...prev,
        [key]: st,
      };
    });
  }, []);

  /* =========================================================
     Render
  ========================================================= */

  const InfoModal = () => {
    if (!infoModal.open) return null;
    return ReactDOM.createPortal(
      <div
        onClick={() => setInfoModal({ open: false, title: '', content: '' })}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(2,6,23,.45)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: '#fff', borderRadius: '20px',
            boxShadow: '0 30px 80px rgba(0,0,0,.25)',
            width: 'min(480px, 100%)',
            overflow: 'hidden',
          }}
        >
          {/* Header azul */}
          <div style={{
            background: 'linear-gradient(135deg, #1e3a8a, #2563eb)',
            padding: '16px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                background: 'rgba(255,255,255,.2)', borderRadius: '10px',
                padding: '6px', display: 'flex',
              }}>
                <Info size={18} color="#fff" />
              </div>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>
                {infoModal.title}
              </span>
            </div>
            <button
              onClick={() => setInfoModal({ open: false, title: '', content: '' })}
              style={{
                background: 'rgba(255,255,255,.15)', border: 'none',
                borderRadius: '999px', width: '30px', height: '30px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: '#fff',
              }}
            >
              ✕
            </button>
          </div>

          {/* Contenido */}
          <div style={{ padding: '20px 24px 24px', color: '#374151', fontSize: '.95rem', lineHeight: 1.6 }}>
            <div style={{
              maxHeight: '240px',
              overflowY: 'auto',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
            }}>
              {infoModal.content}
            </div>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setInfoModal({ open: false, title: '', content: '' })}
                style={{
                  background: '#eff6ff', color: '#2563eb', border: 'none',
                  borderRadius: '10px', padding: '8px 18px',
                  fontWeight: 700, cursor: 'pointer', fontSize: '.88rem',
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  if (loading) return <LoadingSpinner message="Cargando…" />;
  if (!curso)
    return (
      <div className="error-container">
        <p>Curso no encontrado</p>
      </div>
    );

  /* ====== Vista examen_solo (misma UI) ====== */
  if (String(curso?.tipo) === "examen_solo") {
    const minReq = Math.max(
      0,
      Math.min(
        100,
        Number(
          curso?.porcentaje_minimo_examen ??
          curso?.porcentaje_minimo_aprobacion ??
          0,
        ),
      ),
    );
    const estado = String(curso?.estado ?? "inscrito");
    const notaFinal =
      curso?.nota_final != null && !Number.isNaN(Number(curso?.nota_final))
        ? Number(curso?.nota_final)
        : null;
    const intentosMax =
      curso?.examen_intentos_max ?? curso?.intentos_max ?? null;
    const tiempoMin =
      curso?.examen_tiempo_minutos ?? curso?.tiempo_limite_minutos ?? null;

    return (
      <>
        <style>{CSS_EXAM_SOLO}</style>

        <div
          className="exSolo-wrap"
          aria-live="polite"
          role="region"
          aria-label="Curso solo examen"
        >
          <section className="exSolo-hero" aria-labelledby="exSolo-title">
            <div className="exSolo-head">
              <div>
                <div className="exSolo-title">
                  <span className="exSolo-badge">
                    <Trophy size={16} /> Examen final
                  </span>
                  <h1 id="exSolo-title">{curso?.nombre ?? "Curso"}</h1>
                </div>
                <p className="exSolo-sub">
                  Este curso es <strong>solo de evaluación</strong>. Desde aquí
                  podrás <strong>agendar</strong> o <strong>rendir</strong> tu
                  examen, revisar requisitos y ver tus intentos disponibles.
                </p>
              </div>

              <div
                className="exSolo-ctaRow"
                style={{ justifyContent: "flex-end" }}
              >
                <button
                  className="exSolo-btn exSolo-btn--primary"
                  onClick={() => navigate(`/curso/${codigoCurso}/examen`)}
                  aria-label="Ir a rendir el examen"
                >
                  <Play size={16} /> Ir a Examen
                </button>
              </div>
            </div>

            <div
              className="exSolo-grid"
              role="group"
              aria-label="Resumen del examen"
            >
              <div className="exSolo-col-left">
                <div className="exSolo-card" aria-labelledby="exSolo-req-title">
                  <div className="exSolo-cardHeader">
                    <div className="exSolo-iconBox" aria-hidden="true">
                      <ShieldCheck size={18} />
                    </div>
                    <h3 id="exSolo-req-title" className="exSolo-cardTitle">
                      Requisitos y recomendaciones
                    </h3>
                  </div>

                  <ul className="exSolo-steps">
                    <li className="exSolo-step">
                      <div className="exSolo-iconBox" aria-hidden="true">
                        <Info size={16} />
                      </div>
                      <p>
                        Verifica tu conexión a internet y usa un navegador
                        actualizado.
                      </p>
                    </li>
                    <li className="exSolo-step">
                      <div className="exSolo-iconBox" aria-hidden="true">
                        <Clock size={16} />
                      </div>
                      <p>
                        El tiempo corre <strong>a partir de iniciar</strong> el
                        examen (si aplica límite).
                      </p>
                    </li>
                    <li className="exSolo-step">
                      <div className="exSolo-iconBox" aria-hidden="true">
                        <CalendarDays size={16} />
                      </div>
                      <p>
                        Si necesitas reagendar, hazlo antes del inicio de tu
                        franja horaria (según políticas).
                      </p>
                    </li>
                  </ul>

                  <div className="exSolo-ctaRow">
                    <button
                      className="exSolo-btn exSolo-btn--ghost"
                      onClick={() => navigate(`/curso/${codigoCurso}/examen`)}
                      aria-label="Abrir pantalla de examen"
                    >
                      <Info size={16} /> Ver detalles del examen
                    </button>
                  </div>

                  <div className="exSolo-hint" role="note">
                    <Info size={16} aria-hidden="true" />
                    <span>
                      En la pantalla del examen podrás <strong>agendar</strong>{" "}
                      o <strong>rendir</strong> según corresponda.
                    </span>
                  </div>
                </div>
              </div>

              <div className="exSolo-col-right">
                <div
                  className="exSolo-card"
                  aria-labelledby="exSolo-metrics-title"
                >
                  <div className="exSolo-cardHeader">
                    <div className="exSolo-iconBox" aria-hidden="true">
                      <Trophy size={18} />
                    </div>
                    <h3 id="exSolo-metrics-title" className="exSolo-cardTitle">
                      Métricas del examen
                    </h3>
                  </div>

                  <div className="exSolo-meta">
                    <div
                      className="exSolo-item"
                      role="group"
                      aria-label="Aprobación mínima"
                    >
                      <div className="exSolo-iconBox" aria-hidden="true">
                        <ShieldCheck size={16} />
                      </div>
                      <div>
                        <strong>{minReq ? `${minReq}%` : "—"}</strong>
                        <br />
                        <span>Aprobación mínima</span>
                      </div>
                    </div>

                    <div
                      className="exSolo-item"
                      role="group"
                      aria-label="Estado de inscripción"
                    >
                      <div className="exSolo-iconBox" aria-hidden="true">
                        <Info size={16} />
                      </div>
                      <div>
                        <strong style={{ textTransform: "capitalize" }}>
                          {estado}
                        </strong>
                        <br />
                        <span>Estado</span>
                      </div>
                    </div>

                    <div
                      className="exSolo-item"
                      role="group"
                      aria-label="Intentos máximos"
                    >
                      <div className="exSolo-iconBox" aria-hidden="true">
                        <Play size={16} />
                      </div>
                      <div>
                        <strong>{intentosMax ?? "—"}</strong>
                        <br />
                        <span>Intentos máx.</span>
                      </div>
                    </div>

                    <div
                      className="exSolo-item"
                      role="group"
                      aria-label="Tiempo límite"
                    >
                      <div className="exSolo-iconBox" aria-hidden="true">
                        <Clock size={16} />
                      </div>
                      <div>
                        <strong>{tiempoMin ? `${tiempoMin} min` : "—"}</strong>
                        <br />
                        <span>Tiempo límite</span>
                      </div>
                    </div>

                    <div
                      className="exSolo-item"
                      role="group"
                      aria-label="Nota final"
                    >
                      <div className="exSolo-iconBox" aria-hidden="true">
                        <Trophy size={16} />
                      </div>
                      <div>
                        <strong>
                          {notaFinal != null ? `${notaFinal}` : "—"}
                        </strong>
                        <br />
                        <span>Nota final</span>
                      </div>
                    </div>

                    <div
                      className="exSolo-item"
                      role="group"
                      aria-label="Código del curso"
                    >
                      <div className="exSolo-iconBox" aria-hidden="true">
                        <Info size={16} />
                      </div>
                      <div>
                        <strong>{curso?.codigo_curso ?? "—"}</strong>
                        <br />
                        <span>Código del curso</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </>
    );
  }


  /* ====== Vista normal ====== */
  return (
    <>
      <style>{CSS_MAIN}</style>

      <div className="course-detail">
        {/* Sentinela + HERO sticky comprimible */}
        <div
          id={`hero-sentinel-${codigoCurso}`}
          className="hero-sentinel"
          aria-hidden="true"
        />
        {/* Panel de Gestión: solo admin, docente, admin_facultad, asistente */}
        {isManagement && (
          <div style={{
            background: '#f1f5f9',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            marginBottom: '16px',
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}>
            {/* Header colapsable */}
            <div
              style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '10px 14px',
                cursor: 'pointer',
                transition: 'background .15s',
              }}
              onClick={() => setManagementExpanded(prev => !prev)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Settings size={16} style={{ color: '#64748b' }} />
                <span style={{
                  fontWeight: 700, fontSize: '12px',
                  color: '#475569', textTransform: 'capitalize',
                }}>
                  Gestión De Curso ({userRole})
                </span>
              </div>
              <button
                style={{
                  background: 'none', border: 'none',
                  color: '#94a3b8', cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                  padding: '4px', borderRadius: '999px',
                }}
                aria-label={managementExpanded ? 'Contraer panel' : 'Expandir panel'}
                type="button"
              >
                {managementExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>

            {/* Contenido colapsable */}
            {managementExpanded && (
              <div style={{
                padding: '0 12px 12px',
                display: 'flex', flexWrap: 'wrap',
                alignItems: 'center', justifyContent: 'space-between', gap: '10px',
              }}>
                {/* Botones simular progreso */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>
                    Simular Progreso:
                  </span>
                  {[
                    { label: 'Inicio (0%)', value: 'start' },
                    { label: 'Mitad (Test Pendiente)', value: 'mid' },
                    { label: 'Completado (100%)', value: 'full' },
                  ].map(({ label, value }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setCourseSimulation(value)}
                      style={{
                        padding: '6px 12px', borderRadius: '8px', fontSize: '11px',
                        fontWeight: 700, cursor: 'pointer',
                        border: courseSimulation === value ? 'none' : '1px solid #e2e8f0',
                        background: courseSimulation === value ? '#2563eb' : '#fff',
                        color: courseSimulation === value ? '#fff' : '#475569',
                        transition: 'all .15s',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Acciones: Reportes + Editar Curso */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: '#fff', border: '1px solid #e2e8f0',
                  borderRadius: '12px', padding: '4px 8px',
                }}>
                  <button
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '6px 12px', borderRadius: '8px', fontSize: '11px',
                      fontWeight: 700, background: '#eef2ff', color: '#4338ca',
                      border: 'none', cursor: 'pointer',
                    }}
                    type="button"
                  // TODO: conectar a tu lógica de reportes
                  >
                    <FileText size={14} /> Reportes
                  </button>

                </div>
              </div>
            )}
          </div>
        )}
        <div className="sticky top-0 z-40 pt-3 pb-3">
          <div className="flex flex-col md:flex-row gap-3 items-stretch w-full" data-tour="hero-left">

            {/* Tarjeta izquierda: título + progreso */}
            <div className="flex-grow bg-white rounded-3xl p-3 sm:p-4 md:p-5 shadow-sm border border-blue-50 flex flex-row items-center justify-between gap-4 text-left">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 md:gap-3">
                <h1
                  style={{
                    margin: 0,
                    color: 'var(--brand)',
                    fontSize: 'clamp(16px, 2.5vw, 22px)',
                    fontWeight: 900,
                    letterSpacing: '.2px',
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                  }}
                >
                  {curso.nombre}
                </h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="code-pill">{curso.codigo_curso}</span>
                  <button
                    className="mini-link pulse-eye"
                    onClick={() => setInfoModal({
                      open: true,
                      title: curso.nombre,
                      content: curso.descripcion || 'Este curso aún no tiene descripción.',
                    })}
                    title="Ver información del curso"
                  >
                    <Info size={16} />
                  </button>
                </div>

                {heroOpen && !stuck && (
                  <div id="hero-extra" style={{ marginTop: '8px' }}>
                    <p className="hero-desc">
                      {curso.descripcion || 'Este curso aún no tiene descripción.'}
                    </p>
                    <div className="hero-hints">
                      <Info size={13} />
                      <span className="muted">Completa módulos para desbloquear nuevos componentes.</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Anillo de progreso - estilo Tailwind */}
              <div className="relative w-20 h-20 sm:w-20 sm:h-20 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-white shadow-md border border-blue-50/50 flex-shrink-0">
                <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                  <circle cx="50%" cy="50%" r="42%" fill="none" className="stroke-gray-100" strokeWidth="6" />
                  <circle
                    cx="50%" cy="50%" r="42%" fill="none"
                    className={`transition-all duration-1000 ease-out ${ringValue >= 100 ? 'stroke-emerald-500' :
                      ringValue >= 75 ? 'stroke-lime-500' :
                        ringValue >= 33 ? 'stroke-yellow-400' :
                          ringValue > 0 ? 'stroke-amber-400' :
                            'stroke-gray-300'
                      }`}
                    strokeWidth="6"
                    pathLength="100"
                    strokeDasharray="100"
                    strokeDashoffset={Math.max(0, 100 - (ringValue ?? 0))}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="flex flex-col items-center leading-none z-10">
                  <span className="text-[7px] md:text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Progreso</span>
                  <span className={`text-sm sm:text-base md:text-lg font-black ${ringValue >= 100 ? 'text-emerald-600' :
                    ringValue >= 75 ? 'text-lime-600' :
                      ringValue >= 33 ? 'text-yellow-600' :
                        ringValue > 0 ? 'text-amber-500' :
                          'text-gray-400'
                    }`}>{ringValue ?? 0}%</span>
                </div>
              </div>
            </div>



            {/* Tarjeta derecha: Racha + UTMCoins */}
            <div
              className="bg-white rounded-3xl shadow-sm border border-blue-50 flex items-center gap-6 justify-center flex-shrink-0 w-full md:w-auto"
              style={{ padding: '16px 28px' }}
              data-tour="streak-card"
            >
              {/* Racha */}
              {(() => {
                const r = studentStreak?.racha ?? studentStreak ?? {};
                const current = Number(r.current ?? r.racha_actual_dias ?? 0);
                const lastClaimYMD = r.lastClaimYMD ?? r.last_claim_ymd ?? null;
                const todayYMD = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' });
                const claimed = !!(r.claimedToday ?? (lastClaimYMD === todayYMD));
                const eligibleToday = !!(r.eligibleToday ?? r.eligible_today);
                const canClaim = !claimed && eligibleToday;

                return (
                  <div
                    onClick={() => setOpenStreak(true)}
                    className="flex flex-col items-center gap-1.5 cursor-pointer hover:scale-105 transition-transform group"
                    title="Ver racha diaria"
                  >
                    <div className={`
          w-14 h-14 rounded-full border flex flex-col items-center justify-center shadow-inner gap-0.5 transition-colors
          ${claimed
                        ? 'bg-orange-50 border-orange-200 group-hover:bg-orange-100'
                        : canClaim
                          ? 'bg-orange-50 border-orange-300 group-hover:bg-orange-100 animate-pulse'
                          : 'bg-gray-100 border-gray-200 group-hover:bg-gray-200'}
        `}>
                      <span className={`text-lg leading-none ${claimed || canClaim ? '' : 'grayscale'}`}>
                        🔥
                      </span>
                      <span className={`text-sm font-black leading-none ${claimed || canClaim ? 'text-orange-600' : 'text-gray-400'}`}>
                        {current}
                      </span>
                    </div>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider transition-colors ${claimed
                      ? 'text-orange-600 bg-orange-50 border-orange-100'
                      : canClaim
                        ? 'text-red-600 bg-red-50 border-red-200'
                        : 'text-gray-400 bg-gray-100 border-gray-200'
                      }`}>
                      {canClaim ? '¡Reclamar!' : 'Racha'}
                    </span>
                  </div>
                );
              })()}

              {/* Divisor */}
              <div className="w-px h-12 bg-blue-100" />

              {/* UTMCoins */}
              <div className="flex flex-col items-center gap-1.5">
                <div className="bg-blue-50 w-14 h-14 rounded-full border border-blue-100 flex flex-col items-center justify-center shadow-inner gap-0.5">
                  <span className="text-lg leading-none">🪙</span>
                  <span className="text-sm font-black text-blue-600 leading-none">
                    {(() => {
                      const c = utmCoins?.cantidad_actual;
                      const n = typeof c === 'string' ? parseFloat(c) : Number(c);
                      return Number.isFinite(n) ? n : 0;
                    })()}
                  </span>
                </div>
                <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100 uppercase tracking-wider">
                  UTMCoins
                </span>
              </div>
            </div>

          </div>
        </div>
        {/* ESTRUCTURA */}
        <div className="course-structure">
          {estructura.length > 0 ? (
            estructura.map((nivel) => (
              <LevelSection
                key={nivel.id}
                nivel={nivel}
                codigoCurso={codigoCurso}
                isLevelCompleted={isLevelCompleted}
                isLevelStructureCompleted={isLevelStructureCompleted}
                getStructureProgress={getStructureProgress}
                calculateRealProgress={calculateRealProgress}
                onDownloadLevelReport={handleDownloadLevelReport}
                downloadingLevelId={downloadingLevelId}
                canDownloadReport={(() => {
                  const st = levelTestStatusMap?.[Number(nivel.id)];
                  if (!st) return false;
                  const usados = Number(
                    st.usados ?? st.used ?? st.usadosFinales ?? 0,
                  );
                  return usados > 0 || st.status === "approved";
                })()}
                onTestStatus={(st) => handleTestStatusChange(Number(nivel.id), st)}
                refreshKey={location.key}
                onOpenInfo={(title, content, modulos) => setInfoModal({
                  open: true,
                  title,
                  content: (
                    <div>
                      <p style={{ color: '#475569', marginBottom: modulos?.length ? '16px' : 0 }}>
                        {content}
                      </p>
                      {modulos?.length > 0 && (
                        <div>
                          <p style={{ fontSize: '10px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '10px' }}>
                            Módulos contenidos:
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {modulos.map(m => (
                              <div key={m.id} style={{
                                background: '#f8fafc', borderRadius: '12px',
                                border: '1px solid #e2e8f0', padding: '12px 14px',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: m.descripcion ? '4px' : 0 }}>
                                  <Layers size={14} style={{ color: '#0f5fb8', flexShrink: 0 }} />
                                  <span style={{ fontWeight: 700, fontSize: '.9rem', color: '#1e3a8a' }}>{m.nombre}</span>
                                </div>
                                {m.descripcion && (
                                  <p style={{ margin: '4px 0 0 22px', fontSize: '.82rem', color: '#64748b', lineHeight: 1.5 }}>
                                    {m.descripcion}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
                typeStyle={typeStyle}
                filter={filter}
                setFilter={setFilter}
                openLevels={openLevels}
                setOpenLevels={setOpenLevels}
                navigate={navigate}
                filterComponents={filterComponents}
                getIcon={getIcon}
                isCompletedItem={isCompletedItem}
                isUnlockedItem={isUnlockedItem}
                handleComponentClick={handleComponentClick}
                registerRef={registerRef}
                location={location}
              />
            ))
          ) : (
            <div className="empty-state">
              <BookOpen className="empty-icon" />
              <h3>No hay estructura definida</h3>
              <p>
                Cuando el docente agregue niveles y módulos, podrás verlos aquí.
              </p>
            </div>
          )}
        </div>

        {/* Modal de Racha */}
        <StreakModal
          open={openStreak}
          onClose={() => setOpenStreak(false)}
          totalDays={100}
        />

        {/* FAB “Continuar” */}
        {continueMeta.show && nextTarget && (
          <button
            className={`continue-fab ${continueMeta.dir === "up" ? "is-up" : ""}`}
            onClick={scrollToNextTarget}
            aria-label={`Continuar en ${nextTarget?.nombre ?? "siguiente"}`}
            title={`Continuar en ${nextTarget?.nombre ?? "siguiente"}`}
            type="button"
          >
            <span className="cf-ico" aria-hidden="true">
              {continueMeta.dir === "up" ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              )}
            </span>

            <span className="cf-text">
              <span className="cf-title">Continuar</span>
              <span className="cf-sub">
                {nextTarget?.nombre ?? "Siguiente componente"}
              </span>
            </span>
          </button>
        )}

        <TourOverlay
          open={tourOpen}
          step={tourStep}
          steps={TOUR_STEPS}
          targetEl={TOUR_STEPS?.[tourStep]?.target?.()}
          onClose={() => setTourOpen(false)}
          tutorialKey={tutorialKey}
          showInfo={showTourInfo}
          setShowInfo={setShowTourInfo}
          onBack={() => {
            setTourStep((s) => {
              const prev = Math.max(0, s - 1);
              if (TOUR_STEPS?.[prev]?.key !== "first_component") {
                setTourLockFirstClick(false);
              }
              return prev;
            });
          }}
          onNext={() => {
            setTourStep((s) => {
              const next = Math.min(TOUR_STEPS.length - 1, s + 1);

              // ✅ Solo activar lock en el ÚLTIMO paso (first_component)
              if (TOUR_STEPS?.[next]?.key === "first_component") {
                setTourLockFirstClick(true);
              } else {
                setTourLockFirstClick(false);
              }

              return next;
            });
          }}
          forceTargetClick={tourLockFirstClick}
        />
        <TourInfoModal
          open={showTourInfo}
          onClose={() => setShowTourInfo(false)}
        />
        <InfoModal />
      </div>
    </>
  );
};

export default CursoDetalle;
